// tslint:disable: class-name
import { Injectable } from '@angular/core';
import { ConfigService } from 'providers/config.service';
import { AuthService } from 'providers/auth.service';
import { HttpClient, HttpParams } from '@angular/common/http';

@Injectable()
export class OcpiApiService {
    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private config: ConfigService
    ) {}

    /**
     * Get connection profile locations
     */
    async getProfileLocations(connectionProfileId: string): Promise<GetProfileLocationsResponse> {
        return this.http
            .get<GetProfileLocationsResponse>(
                `${this.config.restApiUrl}/ocpi/proxy/profile/${encodeURIComponent(connectionProfileId)}/locations`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Sync connection profile info
     */
    async syncConnectionProfile(connectionProfileId: string, body: SyncConnectionProfileRequest): Promise<any> {
        return this.http
            .post<any>(
                `${this.config.restApiUrl}/ocpi/proxy/profile/${encodeURIComponent(connectionProfileId)}/sync`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Commands handler
     */
    async commands(command: string, body: CommandsRequest): Promise<CommandsResponse> {
        return this.http
            .post<CommandsResponse>(
                `${this.config.restApiUrl}/ocpi/2.2/commands/${encodeURIComponent(command)}`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Pull all Tokens From EMSP
     */
    async manageTokenPull(
        connectionProfileId: string,
        command: PULL_ACTION,
        jobId?: string,
        dateFrom?: string,
        dateTo?: string,
        status?: PullTokensStatusBase['status'],
        numJobs?: number
    ) {
        let params = new HttpParams();

        // Append parameters if they are defined
        if (jobId) params = params.append('jobId', jobId);
        if (dateFrom) params = params.append('dateFrom', dateFrom);
        if (dateTo) params = params.append('dateTo', dateTo);
        if (status) params = params.append('status', status);
        if (numJobs) params = params.append('numJobs', numJobs.toString());

        return this.http // do not update job manually, body should be null
            .post<ManageTokenPullResponse>(
                `${this.config.restApiUrl}/ocpi/profiles/${encodeURIComponent(connectionProfileId)}/pulltokens/${encodeURIComponent(command)}`,
                null,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                    params,
                }
            )
            .toPromise();
    }

    /**
     * Get location
     */
    async getLocation(country_code: string, party_id: string, location_id: string): Promise<GetLocationResponse> {
        return this.http
            .get<GetLocationResponse>(
                `${this.config.restApiUrl}/ocpi/2.2/locations/${encodeURIComponent(country_code)}/${encodeURIComponent(party_id)}/${encodeURIComponent(location_id)}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }
}

export interface GetProfileLocationsResponse {
    status_code:
        | 1000
        | 1900
        | 2000
        | 2001
        | 2002
        | 2003
        | 2004
        | 2900
        | 3000
        | 3001
        | 3002
        | 3003
        | 3900
        | 4001
        | 4002
        | 4003
        | 4900;
    data?: Location[] | null;
    status_message?: string | null;
    timestamp: string;
}

export interface SyncConnectionProfileRequest {
    type: 'stations';
    stationIds?: string[];
}

export interface ChargingStationsApiService {
    axios: AxiosInstance;
    baseURL: string;
    jwtToken: string;
}

export interface DeleteChargingStationResponse {
    success: true;
}

export interface DeleteChargingStationAttachmentResponse {
    success: boolean;
}

export interface Device {
    /**
     * Contains various details about the device
     */
    details?: OmegaDetails | PowerModuleDetails;
    configurationId?: number;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    /**
     * Serial number of the device, e.g. 2400768.
     */
    serialNumber: string;
    /**
     * Device type
     */
    deviceTypeShort: 'cs' | 'ledc' | 'pm' | 'rfid' | 'sc';
    /**
     * Device model name (from manufacturer's ERP), e.g. KEMPOWER C-500 H1
     */
    modelName?: string;
    satelliteId?: number;
    /**
     * If true, the charger has not reported on this device and it might be actually exist
     */
    stale?: boolean;
}

export interface ChargingStation {
    name: string;
    customer?: {
        name?: string;
        id?: string;
    };
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    firmwareVersion?: string;
    /**
     * ID a customer has given to this station
     */
    customerSuppliedId?: string;
    location?: {
        name?: string;
        id?: string;
        address?: string;
        lat?: number;
        lng?: number;
    };
    model?: string;
    vendorName?: string;
    operator?: string;
    notes?: string;
}

export interface Connection {
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    /**
     * Unique connection identifier.
     */
    connectionId: string;
    /**
     * Connection start time in ISO8601 format,
     * e.g. 2019-04-10T06:10:23.234Z
     */
    connectionStartTime: string;
    /**
     * Time when connection was ended or disconnected in ISO8601 format.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    connectionEndTime?: string;
    /**
     * IP address of the client
     */
    clientIpAddress: string;
    /**
     * Country code of the client, as detected from IP address (by CloudFront in the 'Cloudfront-Viewer-Country' header).
     * The value is a two-letter country code (ISO 3166-1 alpha 2), like US (for USA) or TW (for Taiwan).
     * See https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2 for a list of countries.
     */
    clientCountryCode?: string;
}

export interface Alert {
    /**
     * When alert became active.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    timestamp: string;
    /**
     * Type of the error (based on OCPP20, if possible)
     */
    type: string;
    /**
     * EVSE unique identifier.
     */
    evseId: string;
    /**
     * Name of the evse/connector
     */
    evseName?: string;
    /**
     * Type of the evse/connector
     */
    evseType?: 'AC' | 'CCS' | 'CHAdeMO' | 'Type2' | 'oppcharge';
    /**
     * Max connector cable current, e.g. 100 or 125 A
     */
    maxCableCurrent?: number;
    /**
     * Maximum power in kilowatts that this connector could output
     */
    maxPowerKw?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics)
     */
    maxPowerkWPresent?: number;
    /**
     * Maximum power in kilowatts that this connector could output (unrounded figure)
     */
    maxPowerkWActual?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics) (unrounded figure)
     */
    maxPowerkWPresentActual?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed
     */
    maxPowerKwWithoutDynamicLimit?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed (unrounded figure)
     */
    maxPowerKwWithoutDynamicLimitActual?: number;
    /**
     * Actual EVSE identifier - that is, EVSE as in can output to one EV at a time.
     * If multiple "chargeye evses" share this same evseNumber then they belong to the same EVSE.
     * This can be the case for e.g. X-satellites or some Trolleys configured as Sequential.
     */
    actualEVSEId?: number;
    /**
     * Maximum voltage this EVSE can output (500 or 800V)
     */
    maxVoltage?: number;
    /**
     * Used to deduce whether or not the evse is currently in active configuration
     */
    configurationId?: number;
    /**
     * Config - Static text to display instead of the Maximum power in kW (e.g. 40 kW)
     */
    MaxPowerDisplay?: string;
    /**
     * Config -Short additional text to display on the charger
     */
    SupplementaryNote?: string;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    /**
     * Error code
     */
    errorCode?: string;
    /**
     * Error message, or other details
     */
    message?: string;
}

export type Status =
    | 'AVAILABLE'
    | 'BLOCKED'
    | 'CHARGING'
    | 'INOPERATIVE'
    | 'OUTOFORDER'
    | 'PLANNED'
    | 'REMOVED'
    | 'RESERVED'
    | 'UNKNOWN';

export interface SystemMetrics {
    /**
     * When the metrics were recorded.
     */
    timestamp: string;
    type: 'CABINET' | 'SATELLITE' | 'SLAVE_CABINET' | 'STANDALONE' | 'TROLLEY';
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    /**
     * Satellite id in which these were measured, or 0 for the cabinet
     */
    satelliteId: number;
    metrics: SatelliteMetrics;
    additionalMetrics?: {
        pmcCount?: number;
        pmcChargingCount?: number;
    };
}

export interface SystemStructure {
    /**
     * When the structure were recorded.
     */
    timestamp: string;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    structure: any;
}

export interface Settings {
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    controllingSystem?: 'cloud' | 'device';
    settingsSource?: string;
    configuration: BaseConfiguration;
    externalCloudConfiguration?: ExternalCloudConfiguration;
}

export interface IntegrationSettings {
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    /**
     * Collection of ids for the ocpi connection profiles
     */
    ocpiConnectionProfileIds: string[];
}

export interface GetChargingStationResponse {
    alert?: PickAlertRow;
    station: PickChargingStationRow;
    connection?: PickConnectionRow;
    status?: PickStatusRow;
    devices?: {
        /**
         * Contains various details about the device
         */
        details?: OmegaDetails | PowerModuleDetails;
        configurationId?: number;
        /**
         * Station unique identifier.
         */
        stationId: string;
        groupPath?: string;
        /**
         * Serial number of the device, e.g. 2400768.
         */
        serialNumber: string;
        /**
         * Device type
         */
        deviceTypeShort: 'cs' | 'ledc' | 'pm' | 'rfid' | 'sc';
        /**
         * Device model name (from manufacturer's ERP), e.g. KEMPOWER C-500 H1
         */
        modelName?: string;
        satelliteId?: number;
        /**
         * If true, the charger has not reported on this device and it might be actually exist
         */
        stale?: boolean;
    }[];
    metrics?: PickSystemMetricsRow[];
    structure?: PickSystemStructureRow;
    settings?: PickSettingsRow;
    integrationSettings?: PickIntegrationSettingsRow;
}

export interface GetChargingStationQueryParameters {
    stationTenants?: boolean;
}

export interface GetChargingStationAttachmentsResponse {
    attachments: StationAttachment[];
}

export interface GetChargingStationTenantsResponse {
    tenantIdEMSP?: string;
    tenantIdCPO?: string;
    tenantIdMaintenance?: string;
}

export interface GetChargingStationsDefaultResponse {
    chargingStations: {
        name: string;
        /**
         * ID a customer has given to this station
         */
        customerSuppliedId?: string;
        customer?: {
            name?: string;
            id?: string;
        };
        location?: {
            name?: string;
            id?: string;
            address?: string;
            lat?: number;
            lng?: number;
        };
        model?: string;
        vendorName?: string;
        firmwareVersion?: string;
        operator?: string;
        notes?: string;
        /**
         * Station unique identifier.
         */
        stationId: string;
        groupPath?: string;
        _pk: string;
        _sk: string;
        _gsiTenantEMSPPk: string;
        _gsiTenantEMSPSk: string;
        _gsiTenantCPOPk: string;
        _gsiTenantCPOSk: string;
        _gsiTenantMaintenancePk: string;
        _gsiTenantMaintenanceSk: string;
        _gsi1pk: string;
        _gsi1sk: string;
        tenantIdEMSP: string;
        tenantIdCPO: string;
        tenantIdMaintenance: string;
        schema: RowTypes;
        schemaVersion?: number;
        versionCreatedTimestamp: string;
        versionCreatedUserId: string;
        versionModifiedTimestamp: string;
        versionModifiedUserId: string;
        versionDeletedTimestamp?: string;
        versionDeletedUserId: string;
        deleted?: boolean;
    }[];
    lastEvaluatedKey?: {
        [k: string]: any;
    };
}

export interface GetChargingStationsTreeResponse {
    tree: CsmsNode[];
}

export interface GetChargingStationsListResponse {
    tree: {
        status?: CsmsNodeStatus;
        alert?: CsmsNodeAlert;
        children?: CsmsNode[];
        data?: {
            alert?: AlertRow;
            group?: GroupRow;
            station?: {
                name: string;
                /**
                 * ID a customer has given to this station
                 */
                customerSuppliedId?: string;
                customer?: {
                    name?: string;
                    id?: string;
                };
                location?: {
                    name?: string;
                    id?: string;
                    address?: string;
                    lat?: number;
                    lng?: number;
                };
                model?: string;
                vendorName?: string;
                firmwareVersion?: string;
                operator?: string;
                notes?: string;
                /**
                 * Station unique identifier.
                 */
                stationId: string;
                groupPath?: string;
                _pk: string;
                _sk: string;
                _gsiTenantEMSPPk: string;
                _gsiTenantEMSPSk: string;
                _gsiTenantCPOPk: string;
                _gsiTenantCPOSk: string;
                _gsiTenantMaintenancePk: string;
                _gsiTenantMaintenanceSk: string;
                _gsi1pk: string;
                _gsi1sk: string;
                tenantIdEMSP: string;
                tenantIdCPO: string;
                tenantIdMaintenance: string;
                schema: RowTypes;
                schemaVersion?: number;
                versionCreatedTimestamp: string;
                versionCreatedUserId: string;
                versionModifiedTimestamp: string;
                versionModifiedUserId: string;
                versionDeletedTimestamp?: string;
                versionDeletedUserId: string;
                deleted?: boolean;
            };
            evse?: EvseRow;
            evses?: EvseRow[];
            devices?: DeviceRow[];
            status?: StatusRow;
            connection?: ConnectionRow;
        };
        id: string;
        type: 'device' | 'evse' | 'group' | 'station';
        name: string;
        parentGroupPath: string[];
    }[];
}

export interface GetChargingStationsQueryParameters {
    mode?: 'list' | 'tree';
    compact?: boolean;
    limit?: number;
    exclusiveStartKey?: any;
}

export type GetChargingStationsResponse =
    | GetChargingStationsDefaultResponse
    | GetChargingStationsTreeResponse
    | GetChargingStationsListResponse;

export interface GetStationResponse {
    stationId: string;
    /**
     * Path in powerGroup tree, 0th value is the id of root. This station does not belong to a power group if this is null/empty
     */
    powerGroupParentPath?: string[];
    name: string;
    location: {
        name: string;
        address: string;
        lat: number;
        lng: number;
    };
    status: ChargingStationStatus;
    onlineStatus: 'Offline' | 'Online';
    connectors: ConnectorInfo[];
    /**
     * MAX output power (kW) of the station undefined if not known
     */
    maxPowerKw?: number;
    /**
     * Current output power (kW) of the station
     */
    currentPowerKw?: number;
}

export interface GetStationInventoryResponse {
    inventory: any;
    timestamp: string;
}

export interface GetStationsResponse {
    stations: StationInfo[];
}

export interface PostChargingStationRequest {
    stationId?: string;
    /**
     * ID a customer has given to this station
     */
    customerSuppliedId?: string;
    name: string;
    groupPath?: string;
    location?: ChargingStationLocation;
    model?: string;
    vendorName?: string;
    firmwareVersion?: string;
}

export interface ChargingStationLocation {
    name?: string;
    lat?: number;
    lng?: number;
}

export interface PostChargingStationResponse {
    id: string;
}

export interface PostChargingStationAttachmentsRequest {
    attachments: StationAttachment[];
}

export interface PostChargingStationAttachmentsResponse {
    attachments: StationAttachment[];
}

export interface PutChargingStationRequest {
    name?: string;
    customer?: {
        name?: string;
        id?: string;
    };
    groupPath?: string;
    firmwareVersion?: string;
    location?: {
        name?: string;
        id?: string;
        address?: string;
        lat?: number;
        lng?: number;
    };
    model?: string;
    vendorName?: string;
    operator?: string;
    notes?: string;
}

export interface PutChargingStationResponse {
    name: string;
    /**
     * ID a customer has given to this station
     */
    customerSuppliedId?: string;
    customer?: {
        name?: string;
        id?: string;
    };
    location?: {
        name?: string;
        id?: string;
        address?: string;
        lat?: number;
        lng?: number;
    };
    model?: string;
    vendorName?: string;
    firmwareVersion?: string;
    operator?: string;
    notes?: string;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    _pk: string;
    _sk: string;
    _gsiTenantEMSPPk: string;
    _gsiTenantEMSPSk: string;
    _gsiTenantCPOPk: string;
    _gsiTenantCPOSk: string;
    _gsiTenantMaintenancePk: string;
    _gsiTenantMaintenanceSk: string;
    _gsi1pk: string;
    _gsi1sk: string;
    tenantIdEMSP: string;
    tenantIdCPO: string;
    tenantIdMaintenance: string;
    schema: RowTypes;
    schemaVersion?: number;
    versionCreatedTimestamp: string;
    versionCreatedUserId: string;
    versionModifiedTimestamp: string;
    versionModifiedUserId: string;
    versionDeletedTimestamp?: string;
    versionDeletedUserId: string;
    deleted?: boolean;
}

export interface PutChargingStationTenantsRequest {
    tenantIdEMSP?: string;
    tenantIdCPO?: string;
    tenantIdMaintenance?: string;
}

export interface PutChargingStationTenantsResponse {
    success: boolean;
}

export interface PutStationInventoryRequest {
    inventory: any;
}

export interface PutStationInventoryResponse {
    success: boolean;
}

export interface OmegaDetails {
    role?: 'CABINET' | 'SATELLITE' | 'SLAVE_CABINET' | 'STANDALONE' | 'TROLLEY';
    swVersion?: string;
    hwRevision?: string;
}

export interface PowerModuleDetails {
    device?: string;
    family?: string;
    can?: string;
    maxVoltage?: string;
    powerKw?: number;
    manufacturingDate?: string;
    bootVersion?: string;
    applicationVersion?: string;
    featureFlags?: string[];
    boardRevision?: number;
}

export type DeviceTypeShort = 'cs' | 'ledc' | 'pm' | 'rfid' | 'sc';

export type EVSEType = 'AC' | 'CCS' | 'CHAdeMO' | 'Type2' | 'oppcharge';

export interface SatelliteMetrics {
    ts: string;
    satId: number;
    type: 'CABINET' | 'SATELLITE' | 'SLAVE_CABINET' | 'STANDALONE' | 'TROLLEY';
    temp: number;
    tempCore?: number;
    pinTemps: PinTemperatureMetrics;
    cpu: CpuMetrics;
    loadavg: LoadAvgMetrics;
    mem: MemMetrics;
    disk: DiskMetrics;
    swVersion?: string;
    uptime?: number;
    hwRevision?: string;
    serialNumber?: string;
    productCode?: string;
    trigger?: 'Alerting' | 'Periodic';
    pmcStatus?: {
        present?: boolean;
        canAddress: number;
        state?: string;
        boardTemp?: number;
        trTemp?: number;
        dcVoltage?: number;
        canRxOverruns?: number;
        canTxFail?: number;
        alarms?: string[];
        counters?: {
            [k: string]: {
                started: number;
                value: number;
            };
        };
    }[];
    powerStats?: {
        avg: number;
        min: number;
        max: number;
    };
    sshport?: number;
    bootId?: string;
    network?: {
        external?: SatelliteMetricsNetwork;
        internal?: SatelliteMetricsNetwork;
        legacy?: SatelliteMetricsNetwork;
        wired?: SatelliteMetricsNetwork;
        wlan?: SatelliteMetricsNetwork;
    };
    ifstats?: {
        [k: string]: {
            tx: number;
            rx: number;
        };
    };
    cloud?: {
        [k: string]: {
            online: boolean;
            timestamp: string;
            config?: {
                enabled: boolean;
                endpoint: string;
                identity: string;
                token: string;
                tracing?: boolean;
                /**
                 * network interface dedicated for this connection, e.g. if a VPN tunneled connection is required
                 */
                interface?: 'legacyUsbModem' | 'lteExternal' | 'lteInternal' | 'wifi' | 'wired' | 'wiredInternal';
            };
            bufferedCount?: number;
            sendFailuresCount?: number;
            sendFailuresCountPast1h?: number;
            /**
             * Latency in ms of request-responses with ocpp server, of past 1 hour
             */
            latencyStats?: {
                count: number;
                avg: number;
                p10: number;
                median: number;
                p90: number;
            };
        };
    };
}

export interface PinTemperatureMetrics {
    ccs2?: CablePinTemperatureMetrics;
    ccs1?: CablePinTemperatureMetrics;
}

export interface CablePinTemperatureMetrics {
    pin1: number;
    pin2: number;
}

/**
 * parsed mpstat command output
 * 06:24:38     CPU    %usr   %nice    %sys %iowait    %irq   %soft  %steal  %guest  %gnice   %idle
 * 06:24:38     all    1.89    0.32    9.13    0.05    0.00    0.76    0.00    0.00    0.00   87.85
 */
export interface CpuMetrics {
    /**
     * Show the percentage of CPU utilization that occurred while executing at the user level (application).
     */
    usr: number;
    /**
     * Show the percentage of CPU utilization that occurred while executing at the user level with nice priority.
     */
    nice: number;
    /**
     * Show the percentage of CPU utilization that occurred while executing at the system level (kernel).
     * Note that this does not include the time spent servicing interrupts or softirqs.
     */
    sys: number;
    /**
     * Show the percentage of time that the CPU or CPUs were idle during which the system had an outstanding disk I/O request.
     */
    iowait: number;
    /**
     * Show the percentage of time spent by the CPU or CPUs to service interrupts.
     */
    irq: number;
    /**
     * Show the percentage of time spent by the CPU or CPUs to service softirqs.
     * A softirq (software interrupt) is one of up to 32 enumerated software interrupts
     * which can run on multiple CPUs at once.
     */
    soft: number;
    /**
     * Show the percentage of time that the CPU or CPUs were idle and the system did not have an outstanding disk I/O request.
     */
    idle: number;
}

/**
 * parsed /proc/loadavg
 * Load average figures giving the number of jobs in the run queue (state R) or waiting for disk I/O (state D) averaged over 1, 5, and 15 minutes
 */
export interface LoadAvgMetrics {
    _1min: number;
    _5min: number;
    _15min: number;
}

/**
 * /proc/meminfo
 */
export interface MemMetrics {
    MemTotal: number;
    MemFree: number;
    MemAvailable: number;
    Buffers: number;
    Cached: number;
    Mapped: number;
    Shmem: number;
    CommitLimit: number;
    Committed_AS: number;
    VmallocTotal: number;
    VmallocUsed: number;
    VmallocChunk: number;
}

export interface DiskMetrics {
    /**
     * Disk usage for / partition
     */
    root: {
        /**
         * Total size in kilobytes
         */
        size: number;
        /**
         * Size used in kilobytes
         */
        used: number;
        /**
         * Size available in kilobytes
         */
        available: number;
    };
    /**
     * Disk usage for /mnt/data partition
     */
    mntData: {
        /**
         * Total size in kilobytes
         */
        size: number;
        /**
         * Size used in kilobytes
         */
        used: number;
        /**
         * Size available in kilobytes
         */
        available: number;
    };
}

export interface FsMetrics {
    /**
     * Total size in kilobytes
     */
    size: number;
    /**
     * Size used in kilobytes
     */
    used: number;
    /**
     * Size available in kilobytes
     */
    available: number;
}

export interface SatelliteMetricsNetwork {
    interfaceName: string;
    present: boolean;
    /**
     * e.g. 192.168.100.200/24
     */
    ip: string;
    ssid?: string;
    macAddress?: string;
    linkState?: string;
}

export interface BaseConfiguration {
    /**
     * Kempower-specific: Cloud log streaming settings
     */
    LoggingCtrlr: {
        Enabled: boolean;
        EnabledUntil?: string;
        [k: string]: any;
    };
    /**
     * OCPP 2.0: Logical Component responsible for configuration relating to the use of authorization for Charging Station use.
     */
    AuthCtrlr: {
        /**
         * If this variable reports a value of true, Authorization is enabled.
         */
        Enabled: boolean;
        /**
         * OCPP1.6 specific: If Auth not enabled ("free mode"), which tag should we use
         */
        TokenForUnauthenticated?: string;
        /**
         * Whether the Charging Station, when online, will start a transaction for locally-authorized identifiers without waiting for or requesting an AuthorizeResponse from the CSMS.
         */
        LocalPreAuthorize?: boolean;
        /**
         * Whether the Charging Station, when Offline, will start a transaction for locally-authorized identifiers.
         */
        LocalAuthorizeOffline?: boolean;
        /**
         * Whether a remote request to start a transaction in the form of RequestStartTransactionRequest message should be authorized beforehand like a local action to start a transaction.
         */
        AuthorizeRemoteStart?: boolean;
        /**
         * If this key exists, the Charging Station supports Unknown Offline Authorization.
         */
        OfflineTxForUnknownIdEnabled?: boolean;
        /**
         * Kempower specific: If cable is plugged in first and charging is started, how long does user have to authorize before charging is stopped?
         */
        AuthorizationTimeOut?: number;
        [k: string]: any;
    };
    /**
     * OCPP 2.0: Logical Component responsible for configuration relating to the use of Local Authorization Lists for Charging Station use.
     */
    LocalAuthListCtrlr?: {
        /**
         * Whether Local Authorization List is enabled.
         */
        Enabled: boolean;
        [k: string]: any;
    };
    /**
     * OCPP 2.0: Logical Component responsible for configuration relating to the use of a local cache for authorization for Charging Station use.
     */
    AuthCacheCtrlr?: {
        /**
         * If this variable exists, the Charging Station supports an Authorization Cache.
         */
        Enabled: boolean;
        /**
         * Indicates how long it takes until a token expires in the authorization cache since it is last used.
         *
         * Specifies how long (in seconds) after its last use until a token expires and is removed from the authorization cache.
         */
        LifeTime: number;
        [k: string]: any;
    };
    /**
     * Kempower-specific power module settings
     */
    PmcCtrlr: {
        MaximumInputCurrent: number;
        MaximumInputVoltage: number;
        MaximumInputPowerChoices: {
            label: string;
            current: number;
        }[];
        [k: string]: any;
    };
    /**
     * Kempower-specific, panel settings
     */
    DisplayCtrlr: {
        mileageForecast: boolean;
        targetBox: boolean;
        percentageBlurEffect: boolean;
        SettingsEnabled: boolean;
        DemoModeEnabled: boolean;
        DisplayNote?: string;
        MobileQrCodeEnabled: boolean;
        MobileQrCodeLinkUrlPrefix?: string;
        HideStopOnTouchSreen: boolean;
        UiMode: 'dark' | 'light';
        DefaultUiLanguage?: string;
        [k: string]: any;
    };
    SlacRestartCtrlr?: {
        /**
         * Allow charging restart when cable connected (Bus depot mode)
         */
        Enabled: boolean;
        [k: string]: any;
    };
    /**
     * Logical Component responsible for configuration relating to the reporting of clock-aligned meter data.
     */
    AlignedDataCtrlr?: {
        /**
         * If this variable reports a value of true, Aligned Data is enabled.
         */
        Enabled: boolean;
        /**
         * Size (in seconds) of the clock-aligned data interval, intended to be transmitted in the MeterValuesRequest message.
         */
        Interval: number;
        /**
         * Clock-aligned measurand(s) to be included in MeterValuesRequest, every AlignedDataInterval seconds.
         */
        Measurands?: string;
        /**
         * Sampled measurands to be included in the meterValues element of TransactionEventRequest (eventType =
         * Ended), every SampledDataTxEndedInterval seconds from the start of the transaction.
         */
        TxEndedMeasurands?: string;
        [k: string]: any;
    };
    /**
     * Logical Component responsible for configuration relating to the reporting of sampled meter data.
     */
    SampledDataCtrlr?: {
        /**
         * If this variable reports a value of true, Sampled Data is enabled.
         */
        Enabled: boolean;
        /**
         * Interval between sampling of metering (or other) data, intended to be transmitted via TransactionEventRequest (eventType = Updated) messages
         * A value of "0" (numeric zero), by convention, is to be interpreted to mean that no sampled data should be transmitted.
         */
        TxUpdatedInterval: number;
        /**
         * Sampled measurands to be included in the meterValues element of every
         * TransactionEventRequest (eventType = Updated), every SampledDataTxUpdatedInterval
         * seconds from the start of the transaction. Comma separated.
         * Defaults to Energy.Active.Import.Register,SoC,Current.Import,Voltage,Power.Active.Import.
         */
        TxUpdatedMeasurands?: string;
        /**
         * Sampled measurands to be included in the meterValues element of
         * TransactionEventRequest (eventType = Ended), every TxEndedSampleInterval seconds from
         * the start of the transaction.
         * Defaults to Energy.Active.Import.Register,SoC,Current.Import,Voltage,Power.Active.Import.
         */
        TxEndedMeasurands?: string;
        [k: string]: any;
    };
    TariffCostCtrlr: {
        Enabled: boolean;
        /**
         * e.g. € /min
         */
        Units?: string;
        /**
         * e.g. 0.40
         */
        Price?: string;
        [k: string]: any;
    };
    /**
     * Logical Component responsible for configuration relating to transaction characteristics and behaviour.
     */
    TxCtrlr?: {
        /**
         * whether the Charging Station will stop an ongoing transaction when it receives a non-Accepted authorization status in TransactionEventResponse for this transaction
         */
        StopTxOnInvalidId: boolean;
        /**
         * Interval from between "starting" of a transaction until incipient transaction is automatically
         * canceled, due to failure of EV driver to (correctly) insert the charging cable connector(s) into
         * the appropriate socket(s). The Charging Station SHALL go back to the original state,
         * probably: 'Available'. "Starting" might be the swiping of the RFID, pressing a start button, a
         * RequestStartTransactionRequest being received etc.
         */
        EVConnectionTimeOut: number;
        /**
         * (AC only)
         * When set to true, the Charging Station SHALL administratively stop the transaction when the cable is unplugged from the EV.
         * The Charging Cable is unplugged at the EV side. This is detected by the Charging Station. The
         * Charging Station stops the transaction and sends a TransactionEventRequest to the CSMS. The
         * Charging Cable, if locked and UnlockOnEvSideDisconnect = false, will remain locked at the
         * Charging Station until the EV Driver returns and presents his/hers IdToken. Otherwise it will
         * unlock the cable.
         */
        StopTxOnEVSideDisconnect: boolean;
        [k: string]: any;
    };
    /**
     * Logical Component responsible for configuration relating to information exchange between Charging Station and CSMS.
     */
    OCPPCommCtrlr?: {
        /**
         * Interval of inactivity (no OCPP exchanges) with CSMS after which the Charging Station should send HeartbeatRequest.
         */
        HeartbeatInterval: number;
        /**
         * Number of seconds between pings.
         */
        WebSocketPingInterval: number;
        /**
         * Whether to use sequential connector ids (1, 2, 3...) or ids for satellites like 11, 12, 21, 22, etc. Applies to OCPP16J only.
         */
        SequentialConnectorIdsEnabled: boolean;
        /**
         * (AC only) When set to true, the Charging Station SHALL unlock the cable on Charging Station side when the cable is unplugged at the EV.
         */
        UnlockOnEVSideDisconnect: boolean;
        /**
         * How many times the Charging Station should try to submit a TransactionEventRequest message when the CSMS fails to process it
         */
        MessageAttempts: number;
        /**
         * How long the Charging Station should wait before resubmitting a TransactionEventRequest message that the CSMS failed to process.
         */
        MessageAttemptInterval: number;
        [k: string]: any;
    };
    [k: string]: any;
}

export interface ExternalCloudConfiguration {
    enabled: boolean;
    identity: string;
    endpoint: string;
    token?: string;
}

export interface StationAttachment {
    /**
     * Original name of the file
     */
    fileName: string;
    contentType: string;
    fileSize?: number;
    fileId?: string;
    fileKey?: string;
    temporaryUploadUrl?: string;
    temporaryUrl?: string;
}

export type RowTypes =
    | 'LBDecisionLog'
    | 'LBPowerReservationCache'
    | 'LBStation'
    | 'LBStationStatus'
    | 'LBpowerGroup'
    | 'alert'
    | 'alertHistory'
    | 'authorizationList'
    | 'authorizationUserCollection'
    | 'connection'
    | 'connectionHistory'
    | 'counter'
    | 'device'
    | 'deviceHistory'
    | 'ev'
    | 'evSchedule'
    | 'evse'
    | 'firmwarePackage'
    | 'group'
    | 'integrationSettings'
    | 'logStream'
    | 'notificationSettings'
    | 'ocpiConnectionProfile'
    | 'ocpiSequencer'
    | 'ocppMessage'
    | 'ocppRequest'
    | 'settings'
    | 'settingsHistory'
    | 'station'
    | 'stationInventory'
    | 'status'
    | 'statusHistory'
    | 'systemMetrics'
    | 'systemMetricsHistory'
    | 'systemStructure'
    | 'systemStructureHistory'
    | 'ticket'
    | 'ticketComment'
    | 'tx'
    | 'txFeedback'
    | 'txLatest'
    | 'txSample'
    | 'txUpdate'
    | 'updateProcess'
    | 'updateProcessNotification'
    | 'userCollection'
    | 'userCollectionToken'
    | 'v2icp';

export interface CsmsNode {
    status?: CsmsNodeStatus;
    alert?: CsmsNodeAlert;
    children?: CsmsNode[];
    data?: {
        alert?: AlertRow;
        group?: GroupRow;
        station?: {
            name: string;
            /**
             * ID a customer has given to this station
             */
            customerSuppliedId?: string;
            customer?: {
                name?: string;
                id?: string;
            };
            location?: {
                name?: string;
                id?: string;
                address?: string;
                lat?: number;
                lng?: number;
            };
            model?: string;
            vendorName?: string;
            firmwareVersion?: string;
            operator?: string;
            notes?: string;
            /**
             * Station unique identifier.
             */
            stationId: string;
            groupPath?: string;
            _pk: string;
            _sk: string;
            _gsiTenantEMSPPk: string;
            _gsiTenantEMSPSk: string;
            _gsiTenantCPOPk: string;
            _gsiTenantCPOSk: string;
            _gsiTenantMaintenancePk: string;
            _gsiTenantMaintenanceSk: string;
            _gsi1pk: string;
            _gsi1sk: string;
            tenantIdEMSP: string;
            tenantIdCPO: string;
            tenantIdMaintenance: string;
            schema: RowTypes;
            schemaVersion?: number;
            versionCreatedTimestamp: string;
            versionCreatedUserId: string;
            versionModifiedTimestamp: string;
            versionModifiedUserId: string;
            versionDeletedTimestamp?: string;
            versionDeletedUserId: string;
            deleted?: boolean;
        };
        evse?: EvseRow;
        evses?: EvseRow[];
        devices?: DeviceRow[];
        status?: StatusRow;
        connection?: ConnectionRow;
    };
    id: string;
    type: 'device' | 'evse' | 'group' | 'station';
    name: string;
}

export interface CsmsNodeStatus {
    statusDate?: string;
    status?: 'Available' | 'Charging' | 'Faulted' | 'Occupied' | 'Offline' | 'Operative' | 'Reserved' | 'Unavailable';
    alert?: boolean;
    reason?: string;
    warning?: boolean;
    descendantHasAlert?: boolean;
    descendantHasWarning?: boolean;
}

export interface CsmsNodeAlert {
    /**
     * When alert became active.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    timestamp: string;
    /**
     * Type of the error (based on OCPP20, if possible), or NoError
     */
    type: string;
    /**
     * Error code
     */
    errorCode?: string;
    /**
     * Error message, or other details
     */
    message?: string;
}

export interface AlertRow {
    schema: 'alert';
    /**
     * When alert became active.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    timestamp: string;
    /**
     * Type of the error (based on OCPP20, if possible)
     */
    type: string;
    /**
     * Error code
     */
    errorCode?: string;
    /**
     * Error message, or other details
     */
    message?: string;
    /**
     * EVSE unique identifier.
     */
    evseId: string;
    /**
     * Name of the evse/connector
     */
    evseName?: string;
    /**
     * Type of the evse/connector
     */
    evseType?: 'AC' | 'CCS' | 'CHAdeMO' | 'Type2' | 'oppcharge';
    /**
     * Max connector cable current, e.g. 100 or 125 A
     */
    maxCableCurrent?: number;
    /**
     * Maximum power in kilowatts that this connector could output
     */
    maxPowerKw?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics)
     */
    maxPowerkWPresent?: number;
    /**
     * Maximum power in kilowatts that this connector could output (unrounded figure)
     */
    maxPowerkWActual?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics) (unrounded figure)
     */
    maxPowerkWPresentActual?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed
     */
    maxPowerKwWithoutDynamicLimit?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed (unrounded figure)
     */
    maxPowerKwWithoutDynamicLimitActual?: number;
    /**
     * Actual EVSE identifier - that is, EVSE as in can output to one EV at a time.
     * If multiple "chargeye evses" share this same evseNumber then they belong to the same EVSE.
     * This can be the case for e.g. X-satellites or some Trolleys configured as Sequential.
     */
    actualEVSEId?: number;
    /**
     * Maximum voltage this EVSE can output (500 or 800V)
     */
    maxVoltage?: number;
    /**
     * Used to deduce whether or not the evse is currently in active configuration
     */
    configurationId?: number;
    /**
     * Config - Static text to display instead of the Maximum power in kW (e.g. 40 kW)
     */
    MaxPowerDisplay?: string;
    /**
     * Config -Short additional text to display on the charger
     */
    SupplementaryNote?: string;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    _pk: string;
    _sk: string;
    _gsiTenantEMSPPk: string;
    _gsiTenantEMSPSk: string;
    _gsiTenantCPOPk: string;
    _gsiTenantCPOSk: string;
    _gsiTenantMaintenancePk: string;
    _gsiTenantMaintenanceSk: string;
    _gsi1pk: string;
    _gsi1sk: string;
    tenantIdEMSP: string;
    tenantIdCPO: string;
    tenantIdMaintenance: string;
    schemaVersion?: number;
    versionCreatedTimestamp: string;
    versionCreatedUserId: string;
    versionModifiedTimestamp: string;
    versionModifiedUserId: string;
    versionDeletedTimestamp?: string;
    versionDeletedUserId: string;
    deleted?: boolean;
}

export interface GroupRow {
    schema: 'group';
    name: string;
    groupId: string;
    groupPath?: string;
    groupPathInclusive: string;
    _pk: string;
    _sk: string;
    _gsiTenantEMSPPk: string;
    _gsiTenantEMSPSk: string;
    _gsiTenantCPOPk: string;
    _gsiTenantCPOSk: string;
    _gsiTenantMaintenancePk: string;
    _gsiTenantMaintenanceSk: string;
    _gsi1pk: string;
    _gsi1sk: string;
    tenantIdEMSP: string;
    tenantIdCPO: string;
    tenantIdMaintenance: string;
    schemaVersion?: number;
    versionCreatedTimestamp: string;
    versionCreatedUserId: string;
    versionModifiedTimestamp: string;
    versionModifiedUserId: string;
    versionDeletedTimestamp?: string;
    versionDeletedUserId: string;
    deleted?: boolean;
}

export interface EvseRow {
    schema: 'evse';
    /**
     * EVSE unique identifier.
     */
    evseId: string;
    /**
     * Name of the evse/connector
     */
    evseName?: string;
    /**
     * Type of the evse/connector
     */
    evseType?: 'AC' | 'CCS' | 'CHAdeMO' | 'Type2' | 'oppcharge';
    /**
     * Max connector cable current, e.g. 100 or 125 A
     */
    maxCableCurrent?: number;
    /**
     * Maximum power in kilowatts that this connector could output
     */
    maxPowerKw?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics)
     */
    maxPowerkWPresent?: number;
    /**
     * Maximum power in kilowatts that this connector could output (unrounded figure)
     */
    maxPowerkWActual?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics) (unrounded figure)
     */
    maxPowerkWPresentActual?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed
     */
    maxPowerKwWithoutDynamicLimit?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed (unrounded figure)
     */
    maxPowerKwWithoutDynamicLimitActual?: number;
    /**
     * Actual EVSE identifier - that is, EVSE as in can output to one EV at a time.
     * If multiple "chargeye evses" share this same evseNumber then they belong to the same EVSE.
     * This can be the case for e.g. X-satellites or some Trolleys configured as Sequential.
     */
    actualEVSEId?: number;
    /**
     * Maximum voltage this EVSE can output (500 or 800V)
     */
    maxVoltage?: number;
    /**
     * Used to deduce whether or not the evse is currently in active configuration
     */
    configurationId?: number;
    /**
     * Config - Static text to display instead of the Maximum power in kW (e.g. 40 kW)
     */
    MaxPowerDisplay?: string;
    /**
     * Config -Short additional text to display on the charger
     */
    SupplementaryNote?: string;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    _pk: string;
    _sk: string;
    _gsiTenantEMSPPk: string;
    _gsiTenantEMSPSk: string;
    _gsiTenantCPOPk: string;
    _gsiTenantCPOSk: string;
    _gsiTenantMaintenancePk: string;
    _gsiTenantMaintenanceSk: string;
    _gsi1pk: string;
    _gsi1sk: string;
    tenantIdEMSP: string;
    tenantIdCPO: string;
    tenantIdMaintenance: string;
    schemaVersion?: number;
    versionCreatedTimestamp: string;
    versionCreatedUserId: string;
    versionModifiedTimestamp: string;
    versionModifiedUserId: string;
    versionDeletedTimestamp?: string;
    versionDeletedUserId: string;
    deleted?: boolean;
}

export interface DeviceRow {
    /**
     * Row type device
     */
    schema: 'device';
    /**
     * Serial number of the device, e.g. 2400768.
     */
    serialNumber: string;
    /**
     * Device type
     */
    deviceTypeShort: 'cs' | 'ledc' | 'pm' | 'rfid' | 'sc';
    /**
     * Device model name (from manufacturer's ERP), e.g. KEMPOWER C-500 H1
     */
    modelName?: string;
    /**
     * Contains various details about the device
     */
    details?: OmegaDetails | PowerModuleDetails;
    configurationId?: number;
    satelliteId?: number;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    _pk: string;
    _sk: string;
    _gsiTenantEMSPPk: string;
    _gsiTenantEMSPSk: string;
    _gsiTenantCPOPk: string;
    _gsiTenantCPOSk: string;
    _gsiTenantMaintenancePk: string;
    _gsiTenantMaintenanceSk: string;
    _gsi1pk: string;
    _gsi1sk: string;
    tenantIdEMSP: string;
    tenantIdCPO: string;
    tenantIdMaintenance: string;
    schemaVersion?: number;
    versionCreatedTimestamp: string;
    versionCreatedUserId: string;
    versionModifiedTimestamp: string;
    versionModifiedUserId: string;
    versionDeletedTimestamp?: string;
    versionDeletedUserId: string;
    deleted?: boolean;
}

export interface StatusRow {
    schema: 'status';
    /**
     * When status became active.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    statusDate: string;
    /**
     * Status of the EVSE or Charging station.
     */
    status: 'Available' | 'Charging' | 'Faulted' | 'Occupied' | 'Offline' | 'Operative' | 'Reserved' | 'Unavailable';
    /**
     * Reason for why the status is unavailable
     */
    reason?: string;
    /**
     * More detailed reason why status is unavailable
     */
    detail?: string;
    /**
     * EVSE unique identifier.
     */
    evseId: string;
    /**
     * Name of the evse/connector
     */
    evseName?: string;
    /**
     * Type of the evse/connector
     */
    evseType?: 'AC' | 'CCS' | 'CHAdeMO' | 'Type2' | 'oppcharge';
    /**
     * Max connector cable current, e.g. 100 or 125 A
     */
    maxCableCurrent?: number;
    /**
     * Maximum power in kilowatts that this connector could output
     */
    maxPowerKw?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics)
     */
    maxPowerkWPresent?: number;
    /**
     * Maximum power in kilowatts that this connector could output (unrounded figure)
     */
    maxPowerkWActual?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics) (unrounded figure)
     */
    maxPowerkWPresentActual?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed
     */
    maxPowerKwWithoutDynamicLimit?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed (unrounded figure)
     */
    maxPowerKwWithoutDynamicLimitActual?: number;
    /**
     * Actual EVSE identifier - that is, EVSE as in can output to one EV at a time.
     * If multiple "chargeye evses" share this same evseNumber then they belong to the same EVSE.
     * This can be the case for e.g. X-satellites or some Trolleys configured as Sequential.
     */
    actualEVSEId?: number;
    /**
     * Maximum voltage this EVSE can output (500 or 800V)
     */
    maxVoltage?: number;
    /**
     * Used to deduce whether or not the evse is currently in active configuration
     */
    configurationId?: number;
    /**
     * Config - Static text to display instead of the Maximum power in kW (e.g. 40 kW)
     */
    MaxPowerDisplay?: string;
    /**
     * Config -Short additional text to display on the charger
     */
    SupplementaryNote?: string;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    _pk: string;
    _sk: string;
    _gsiTenantEMSPPk: string;
    _gsiTenantEMSPSk: string;
    _gsiTenantCPOPk: string;
    _gsiTenantCPOSk: string;
    _gsiTenantMaintenancePk: string;
    _gsiTenantMaintenanceSk: string;
    _gsi1pk: string;
    _gsi1sk: string;
    tenantIdEMSP: string;
    tenantIdCPO: string;
    tenantIdMaintenance: string;
    schemaVersion?: number;
    versionCreatedTimestamp: string;
    versionCreatedUserId: string;
    versionModifiedTimestamp: string;
    versionModifiedUserId: string;
    versionDeletedTimestamp?: string;
    versionDeletedUserId: string;
    deleted?: boolean;
}

export interface ConnectionRow {
    schema: 'connection';
    /**
     * Unique connection identifier.
     */
    connectionId: string;
    /**
     * Connection start time in ISO8601 format,
     * e.g. 2019-04-10T06:10:23.234Z
     */
    connectionStartTime: string;
    /**
     * Time when connection was ended or disconnected in ISO8601 format.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    connectionEndTime?: string;
    /**
     * IP address of the client
     */
    clientIpAddress: string;
    /**
     * Country code of the client, as detected from IP address (by CloudFront in the 'Cloudfront-Viewer-Country' header).
     * The value is a two-letter country code (ISO 3166-1 alpha 2), like US (for USA) or TW (for Taiwan).
     * See https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2 for a list of countries.
     */
    clientCountryCode?: string;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    _pk: string;
    _sk: string;
    _gsiTenantEMSPPk: string;
    _gsiTenantEMSPSk: string;
    _gsiTenantCPOPk: string;
    _gsiTenantCPOSk: string;
    _gsiTenantMaintenancePk: string;
    _gsiTenantMaintenanceSk: string;
    _gsi1pk: string;
    _gsi1sk: string;
    tenantIdEMSP: string;
    tenantIdCPO: string;
    tenantIdMaintenance: string;
    schemaVersion?: number;
    versionCreatedTimestamp: string;
    versionCreatedUserId: string;
    versionModifiedTimestamp: string;
    versionModifiedUserId: string;
    versionDeletedTimestamp?: string;
    versionDeletedUserId: string;
    deleted?: boolean;
}

export type ChargingStationStatus = 'Faulted' | 'Offline' | 'Operative' | 'Unavailable';

export interface ConnectorInfo {
    connectorId: string;
    type: EVSEType;
    name: string;
    status: EVSEStatus;
    maxPowerKw: number;
    maxPowerKwActual: number;
    latestTransaction: TxInfo;
}

export type EVSEStatus = 'Available' | 'Charging' | 'Faulted' | 'Occupied' | 'Offline' | 'Reserved' | 'Unavailable';

export interface TxInfo {
    txId?: string;
    connectorId?: string;
    connectorName?: string;
    connectorType?: EVSEType;
    authorizationToken?: string;
    status?: TransactionStatus;
    startReason?: string;
    startTime?: string;
    chargingStartReason?: string;
    chargingStartTime?: string;
    chargingEndReason?: string;
    chargingEndTime?: string;
    endReason?: string;
    endTime?: string;
    chargedEnergyKwh?: number;
    energyLimitKwh?: number;
    powerKw?: number;
    powerKwMax?: number;
    powerKwAvg?: number;
    soc?: number;
    socInitial?: number;
    socLimit?: number;
    timeLimitMs?: number;
    triggerReason: string;
    updateTime: string;
    powerLimitation?: 'Charger' | 'Grid' | 'Vehicle';
}

export type TransactionStatus =
    | 'Alarm'
    | 'AuthorizedNotPluggedIn'
    | 'Charging'
    | 'Ended'
    | 'InsulationTest'
    | 'PluggedInIdle'
    | 'Unknown';

export interface StationInfo {
    stationId: string;
    /**
     * Path in powerGroup tree, 0th value is the id of root. This station does not belong to a power group if this is null/empty
     */
    powerGroupParentPath?: string[];
    name: string;
    location: {
        name: string;
        address: string;
        lat: number;
        lng: number;
    };
    status: ChargingStationStatus;
    onlineStatus: 'Offline' | 'Online';
    connectors: ConnectorInfo[];
    /**
     * MAX output power (kW) of the station undefined if not known
     */
    maxPowerKw?: number;
    /**
     * Current output power (kW) of the station
     */
    currentPowerKw?: number;
}

export interface PickAlertRow {
    /**
     * When alert became active.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    timestamp: string;
    /**
     * Type of the error (based on OCPP20, if possible)
     */
    type: string;
    /**
     * EVSE unique identifier.
     */
    evseId: string;
    /**
     * Name of the evse/connector
     */
    evseName?: string;
    /**
     * Type of the evse/connector
     */
    evseType?: 'AC' | 'CCS' | 'CHAdeMO' | 'Type2' | 'oppcharge';
    /**
     * Max connector cable current, e.g. 100 or 125 A
     */
    maxCableCurrent?: number;
    /**
     * Maximum power in kilowatts that this connector could output
     */
    maxPowerKw?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics)
     */
    maxPowerkWPresent?: number;
    /**
     * Maximum power in kilowatts that this connector could output (unrounded figure)
     */
    maxPowerkWActual?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics) (unrounded figure)
     */
    maxPowerkWPresentActual?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed
     */
    maxPowerKwWithoutDynamicLimit?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed (unrounded figure)
     */
    maxPowerKwWithoutDynamicLimitActual?: number;
    /**
     * Actual EVSE identifier - that is, EVSE as in can output to one EV at a time.
     * If multiple "chargeye evses" share this same evseNumber then they belong to the same EVSE.
     * This can be the case for e.g. X-satellites or some Trolleys configured as Sequential.
     */
    actualEVSEId?: number;
    /**
     * Maximum voltage this EVSE can output (500 or 800V)
     */
    maxVoltage?: number;
    /**
     * Used to deduce whether or not the evse is currently in active configuration
     */
    configurationId?: number;
    /**
     * Config - Static text to display instead of the Maximum power in kW (e.g. 40 kW)
     */
    MaxPowerDisplay?: string;
    /**
     * Config -Short additional text to display on the charger
     */
    SupplementaryNote?: string;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    /**
     * Error code
     */
    errorCode?: string;
    /**
     * Error message, or other details
     */
    message?: string;
}

export interface PickChargingStationRow {
    name: string;
    customer?: {
        name?: string;
        id?: string;
    };
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    firmwareVersion?: string;
    /**
     * ID a customer has given to this station
     */
    customerSuppliedId?: string;
    location?: {
        name?: string;
        id?: string;
        address?: string;
        lat?: number;
        lng?: number;
    };
    model?: string;
    vendorName?: string;
    operator?: string;
    notes?: string;
}

export interface PickConnectionRow {
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    /**
     * Unique connection identifier.
     */
    connectionId: string;
    /**
     * Connection start time in ISO8601 format,
     * e.g. 2019-04-10T06:10:23.234Z
     */
    connectionStartTime: string;
    /**
     * Time when connection was ended or disconnected in ISO8601 format.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    connectionEndTime?: string;
    /**
     * IP address of the client
     */
    clientIpAddress: string;
    /**
     * Country code of the client, as detected from IP address (by CloudFront in the 'Cloudfront-Viewer-Country' header).
     * The value is a two-letter country code (ISO 3166-1 alpha 2), like US (for USA) or TW (for Taiwan).
     * See https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2 for a list of countries.
     */
    clientCountryCode?: string;
}

export interface PickStatusRow {
    /**
     * EVSE unique identifier.
     */
    evseId: string;
    /**
     * Name of the evse/connector
     */
    evseName?: string;
    /**
     * Type of the evse/connector
     */
    evseType?: 'AC' | 'CCS' | 'CHAdeMO' | 'Type2' | 'oppcharge';
    /**
     * Max connector cable current, e.g. 100 or 125 A
     */
    maxCableCurrent?: number;
    /**
     * Maximum power in kilowatts that this connector could output
     */
    maxPowerKw?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics)
     */
    maxPowerkWPresent?: number;
    /**
     * Maximum power in kilowatts that this connector could output (unrounded figure)
     */
    maxPowerkWActual?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics) (unrounded figure)
     */
    maxPowerkWPresentActual?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed
     */
    maxPowerKwWithoutDynamicLimit?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed (unrounded figure)
     */
    maxPowerKwWithoutDynamicLimitActual?: number;
    /**
     * Actual EVSE identifier - that is, EVSE as in can output to one EV at a time.
     * If multiple "chargeye evses" share this same evseNumber then they belong to the same EVSE.
     * This can be the case for e.g. X-satellites or some Trolleys configured as Sequential.
     */
    actualEVSEId?: number;
    /**
     * Maximum voltage this EVSE can output (500 or 800V)
     */
    maxVoltage?: number;
    /**
     * Used to deduce whether or not the evse is currently in active configuration
     */
    configurationId?: number;
    /**
     * Config - Static text to display instead of the Maximum power in kW (e.g. 40 kW)
     */
    MaxPowerDisplay?: string;
    /**
     * Config -Short additional text to display on the charger
     */
    SupplementaryNote?: string;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    /**
     * Status of the EVSE or Charging station.
     */
    status: 'Available' | 'Charging' | 'Faulted' | 'Occupied' | 'Offline' | 'Operative' | 'Reserved' | 'Unavailable';
    /**
     * When status became active.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    statusDate: string;
    /**
     * Reason for why the status is unavailable
     */
    reason?: string;
    /**
     * More detailed reason why status is unavailable
     */
    detail?: string;
}

export interface PickSystemMetricsRow {
    /**
     * When the metrics were recorded.
     */
    timestamp: string;
    type: 'CABINET' | 'SATELLITE' | 'SLAVE_CABINET' | 'STANDALONE' | 'TROLLEY';
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    /**
     * Satellite id in which these were measured, or 0 for the cabinet
     */
    satelliteId: number;
    metrics: SatelliteMetrics;
    additionalMetrics?: {
        pmcCount?: number;
        pmcChargingCount?: number;
    };
}

export interface PickSystemStructureRow {
    /**
     * When the structure were recorded.
     */
    timestamp: string;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    structure: any;
}

export interface PickSettingsRow {
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    controllingSystem?: 'cloud' | 'device';
    settingsSource?: string;
    configuration: BaseConfiguration;
    externalCloudConfiguration?: ExternalCloudConfiguration;
}

export interface PickIntegrationSettingsRow {
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    /**
     * Collection of ids for the ocpi connection profiles
     */
    ocpiConnectionProfileIds: string[];
}

export interface EvseApiService {
    axios: AxiosInstance;
    baseURL: string;
    jwtToken: string;
}

export interface DeleteEvseResponse {
    success: true;
}

export interface GetEvsesResponse {
    evses: {
        /**
         * The actual EVSE id as given by the charger. Numbered starting from 1.
         * If multiple "chargeye evses" share this same evseNumber then they belong to the same EVSE.
         * This can be the case for e.g. X-satellites or some Trolleys configured as Sequential.
         */
        actualEVSEId: number;
        /**
         * Full EVSE ID for roaming use, such as FI*KP*E024*1.
         *
         * https://www.realschemes.org.uk/pdf/ev-roam-guidance.pdf
         * http://emi3group.com/wp-content/uploads/sites/5/2018/12/eMI3-standard-v1.0-Part-2.pdf
         */
        roamingEvseId?: string;
        /**
         * Name of the EVSE, e.g. "1" or "F4318".
         * For display purposes only.
         */
        physicalReference?: string;
        connectors: {
            active: boolean;
            schema: 'evse';
            /**
             * EVSE unique identifier.
             */
            evseId: string;
            /**
             * Name of the evse/connector
             */
            evseName?: string;
            /**
             * Type of the evse/connector
             */
            evseType?: 'AC' | 'CCS' | 'CHAdeMO' | 'Type2' | 'oppcharge';
            /**
             * Max connector cable current, e.g. 100 or 125 A
             */
            maxCableCurrent?: number;
            /**
             * Maximum power in kilowatts that this connector could output
             */
            maxPowerKw?: number;
            /**
             * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics)
             */
            maxPowerkWPresent?: number;
            /**
             * Maximum power in kilowatts that this connector could output (unrounded figure)
             */
            maxPowerkWActual?: number;
            /**
             * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics) (unrounded figure)
             */
            maxPowerkWPresentActual?: number;
            /**
             * Maximum power in kilowatts that this connector could output if grid limit was not imposed
             */
            maxPowerKwWithoutDynamicLimit?: number;
            /**
             * Maximum power in kilowatts that this connector could output if grid limit was not imposed (unrounded figure)
             */
            maxPowerKwWithoutDynamicLimitActual?: number;
            /**
             * Actual EVSE identifier - that is, EVSE as in can output to one EV at a time.
             * If multiple "chargeye evses" share this same evseNumber then they belong to the same EVSE.
             * This can be the case for e.g. X-satellites or some Trolleys configured as Sequential.
             */
            actualEVSEId?: number;
            /**
             * Maximum voltage this EVSE can output (500 or 800V)
             */
            maxVoltage?: number;
            /**
             * Used to deduce whether or not the evse is currently in active configuration
             */
            configurationId?: number;
            /**
             * Config - Static text to display instead of the Maximum power in kW (e.g. 40 kW)
             */
            MaxPowerDisplay?: string;
            /**
             * Config -Short additional text to display on the charger
             */
            SupplementaryNote?: string;
            /**
             * Station unique identifier.
             */
            stationId: string;
            groupPath?: string;
            _pk: string;
            _sk: string;
            _gsiTenantEMSPPk: string;
            _gsiTenantEMSPSk: string;
            _gsiTenantCPOPk: string;
            _gsiTenantCPOSk: string;
            _gsiTenantMaintenancePk: string;
            _gsiTenantMaintenanceSk: string;
            _gsi1pk: string;
            _gsi1sk: string;
            tenantIdEMSP: string;
            tenantIdCPO: string;
            tenantIdMaintenance: string;
            schemaVersion?: number;
            versionCreatedTimestamp: string;
            versionCreatedUserId: string;
            versionModifiedTimestamp: string;
            versionModifiedUserId: string;
            versionDeletedTimestamp?: string;
            versionDeletedUserId: string;
            deleted?: boolean;
            status: StatusRow_1;
            alert: AlertRow_1;
            tx: LatestTransactionRow;
        }[];
        status:
            | 'Available'
            | 'Charging'
            | 'Faulted'
            | 'Occupied'
            | 'Offline'
            | 'Operative'
            | 'Reserved'
            | 'Unavailable';
        statusDate: string;
    }[];
}

export type GetEvsesForChargingStationResponse = {
    active: boolean;
    schema: 'evse';
    /**
     * EVSE unique identifier.
     */
    evseId: string;
    /**
     * Name of the evse/connector
     */
    evseName?: string;
    /**
     * Type of the evse/connector
     */
    evseType?: 'AC' | 'CCS' | 'CHAdeMO' | 'Type2' | 'oppcharge';
    /**
     * Max connector cable current, e.g. 100 or 125 A
     */
    maxCableCurrent?: number;
    /**
     * Maximum power in kilowatts that this connector could output
     */
    maxPowerKw?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics)
     */
    maxPowerkWPresent?: number;
    /**
     * Maximum power in kilowatts that this connector could output (unrounded figure)
     */
    maxPowerkWActual?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics) (unrounded figure)
     */
    maxPowerkWPresentActual?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed
     */
    maxPowerKwWithoutDynamicLimit?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed (unrounded figure)
     */
    maxPowerKwWithoutDynamicLimitActual?: number;
    /**
     * Actual EVSE identifier - that is, EVSE as in can output to one EV at a time.
     * If multiple "chargeye evses" share this same evseNumber then they belong to the same EVSE.
     * This can be the case for e.g. X-satellites or some Trolleys configured as Sequential.
     */
    actualEVSEId?: number;
    /**
     * Maximum voltage this EVSE can output (500 or 800V)
     */
    maxVoltage?: number;
    /**
     * Used to deduce whether or not the evse is currently in active configuration
     */
    configurationId?: number;
    /**
     * Config - Static text to display instead of the Maximum power in kW (e.g. 40 kW)
     */
    MaxPowerDisplay?: string;
    /**
     * Config -Short additional text to display on the charger
     */
    SupplementaryNote?: string;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    _pk: string;
    _sk: string;
    _gsiTenantEMSPPk: string;
    _gsiTenantEMSPSk: string;
    _gsiTenantCPOPk: string;
    _gsiTenantCPOSk: string;
    _gsiTenantMaintenancePk: string;
    _gsiTenantMaintenanceSk: string;
    _gsi1pk: string;
    _gsi1sk: string;
    tenantIdEMSP: string;
    tenantIdCPO: string;
    tenantIdMaintenance: string;
    schemaVersion?: number;
    versionCreatedTimestamp: string;
    versionCreatedUserId: string;
    versionModifiedTimestamp: string;
    versionModifiedUserId: string;
    versionDeletedTimestamp?: string;
    versionDeletedUserId: string;
    deleted?: boolean;
    status: StatusRow_1;
    alert: AlertRow_1;
    tx: LatestTransactionRow;
}[];

export interface PutActualEvseRequest {
    /**
     * Full EVSE ID for roaming use, such as FI*KP*E024*1.
     *
     * https://www.realschemes.org.uk/pdf/ev-roam-guidance.pdf
     * http://emi3group.com/wp-content/uploads/sites/5/2018/12/eMI3-standard-v1.0-Part-2.pdf
     */
    roamingEvseId?: string;
    /**
     * Name of the EVSE, e.g. "1" or "F4318".
     * For display purposes only.
     */
    physicalReference?: string;
}

export interface PutActualEvseResponse {
    success: true;
}

export interface PutEvseRequest {
    name?: string;
    configurationId?: number;
    type?: EVSEType;
    maxCableCurrent?: number;
    /**
     * Maximum power in kilowatts that this connector could output
     */
    maxPowerKw?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics)
     */
    maxPowerkWPresent?: number;
    /**
     * Maximum power in kilowatts that this connector could output (unrounded figure)
     */
    maxPowerkWActual?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics) (unrounded figure)
     */
    maxPowerkWPresentActual?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed
     */
    maxPowerKwWithoutDynamicLimit?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed (unrounded figure)
     */
    maxPowerKwWithoutDynamicLimitActual?: number;
    /**
     * Actual EVSE identifier - that is, EVSE as in can output to one EV at a time.
     * If multiple "chargeye evses" share this same evseNumber then they belong to the same EVSE.
     * This can be the case for e.g. X-satellites or some Trolleys configured as Sequential.
     */
    actualEVSEId?: number;
    /**
     * Maximum voltage this EVSE can output (500 or 800V)
     */
    maxVoltage?: number;
    /**
     * Config - Static text to display instead of the Maximum power in kW (e.g. 40 kW)
     */
    MaxPowerDisplay?: string;
    /**
     * Config -Short additional text to display on the charger
     */
    SupplementaryNote?: string;
}

export interface PutEvseResponse {
    success: true;
}

export interface LatestTransactionRow {
    schema: 'txLatest';
    deleted?: boolean;
    /**
     * EVSE unique identifier.
     */
    evseId: string;
    /**
     * Name of the evse/connector
     */
    evseName?: string;
    /**
     * Type of the evse/connector
     */
    evseType?: 'AC' | 'CCS' | 'CHAdeMO' | 'Type2' | 'oppcharge';
    /**
     * Max connector cable current, e.g. 100 or 125 A
     */
    maxCableCurrent?: number;
    /**
     * Maximum power in kilowatts that this connector could output
     */
    maxPowerKw?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics)
     */
    maxPowerkWPresent?: number;
    /**
     * Maximum power in kilowatts that this connector could output (unrounded figure)
     */
    maxPowerkWActual?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics) (unrounded figure)
     */
    maxPowerkWPresentActual?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed
     */
    maxPowerKwWithoutDynamicLimit?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed (unrounded figure)
     */
    maxPowerKwWithoutDynamicLimitActual?: number;
    /**
     * Actual EVSE identifier - that is, EVSE as in can output to one EV at a time.
     * If multiple "chargeye evses" share this same evseNumber then they belong to the same EVSE.
     * This can be the case for e.g. X-satellites or some Trolleys configured as Sequential.
     */
    actualEVSEId?: number;
    /**
     * Maximum voltage this EVSE can output (500 or 800V)
     */
    maxVoltage?: number;
    /**
     * Used to deduce whether or not the evse is currently in active configuration
     */
    configurationId?: number;
    /**
     * Config - Static text to display instead of the Maximum power in kW (e.g. 40 kW)
     */
    MaxPowerDisplay?: string;
    /**
     * Config -Short additional text to display on the charger
     */
    SupplementaryNote?: string;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    _pk: string;
    _sk: string;
    _gsiTenantEMSPPk: string;
    _gsiTenantEMSPSk: string;
    _gsiTenantCPOPk: string;
    _gsiTenantCPOSk: string;
    _gsiTenantMaintenancePk: string;
    _gsiTenantMaintenanceSk: string;
    _gsi1pk: string;
    _gsi1sk: string;
    tenantIdEMSP: string;
    tenantIdCPO: string;
    tenantIdMaintenance: string;
    schemaVersion?: number;
    versionCreatedTimestamp: string;
    versionCreatedUserId: string;
    versionModifiedTimestamp: string;
    versionModifiedUserId: string;
    versionDeletedTimestamp?: string;
    versionDeletedUserId: string;
    status: TransactionStatus;
    txId: string;
    /**
     * Max output power of the evse
     */
    evseMaxPowerKw?: number;
    /**
     * Forecast
     */
    chargingForecast: {
        /**
         * Timestamp when specific SoC (key) is reached
         */
        chargeTimes: {
            '80': string;
            '85': string;
            '90': string;
            '95': string;
            '100': string;
        };
        histogram: {
            /**
             * Time in milliseconds from charging startTimeStamp
             */
            x: number;
            /**
             * SoC prediction
             */
            y: number;
        }[];
    };
    /**
     * Identifier user - id token
     */
    idToken?: string;
    /**
     * Identifier user - how user authenticated?
     */
    idTokenType?: string;
    parentId?: string;
    /**
     * OCPP1.6 remote id
     */
    remoteTransactionId?: number;
    /**
     * OCPP2 remote start id
     */
    remoteStartId?: number;
    /**
     * One of:
     *     * Charger limits - Maximum charging power - Vehicle is charged with the full power of the charger.
     *     * Vehicle limits - Charging power limited by the vehicle - Your vehicle is accepting charging power below the charger capabilities.
     *     * Grid limits - Charging power limited by the electrical grid
     */
    powerLimitation?: 'Charger' | 'Grid' | 'Vehicle';
    startReason?: string;
    startTime?: string;
    chargingStartReason?: string;
    chargingStartTime?: string;
    chargingEndReason?: string;
    chargingEndTime?: string;
    endReason?: string;
    endTime?: string;
    chargingEndAlarms?: string[];
    /**
     * Energy charged into the vehicle
     */
    chargedEnergyKwh?: number;
    energyLimitKwh?: number;
    /**
     * Sequence number to help indentify newest transaction update
     */
    sequenceNumber?: number;
    /**
     * Current power watts
     */
    power?: number;
    /**
     * Power kilowatt present, max, avg
     */
    powerKw?: number;
    avgPowerKw?: number;
    /**
     * Temperature of negative pin of the EVSE in Celsius
     */
    pin1temp?: number;
    /**
     * Temperature of positive pin of the EVSE in Celsius
     */
    pin2temp?: number;
    /**
     * Current SoC
     */
    soc?: number;
    /**
     * State of Charge at the beginning of the transaction
     */
    socStart?: number;
    /**
     * State of Charge at the end of the transaction
     */
    socEnd?: number;
    /**
     * State of charge added during the session - this field makes it easier to search data
     */
    socAdded?: number;
    /**
     * was the transaction soc limited to something
     */
    socLimit?: number;
    /**
     * how long the session was in total (Updated at the end of the session)
     */
    sessionDurationMs?: number;
    /**
     * how long the vehicle was charged (Updated at the end of the session)
     */
    chargingDurationMs?: number;
    /**
     * was the transaction time limited to something
     */
    timeLimitMs?: number;
    /**
     * mac address of the electronic vehicle
     */
    evMacAddress?: string;
    /**
     * run id of the electronic vehicle
     */
    evRunId?: string;
    /**
     * Actual date when changes were made to the transaction
     */
    updateTime?: string;
    /**
     * current and the voltage during the updateTime
     */
    current?: number;
    voltage?: number;
    /**
     * Current/voltage target.
     *
     * NOTE: During charging this is not the actual curren/voltage
     * supplied to the battery but the current/voltage that will
     * eventually be reached once the battery is full.
     */
    targetCurrent?: number;
    targetVoltage?: number;
    /**
     * Maximum voltage EV can take.
     */
    evMaximumVoltage?: number;
    /**
     * Maximum current EV can take.
     */
    evMaximumCurrent?: number;
    /**
     * Maximum power EV can take.
     */
    evMaximumPower?: number;
    /**
     * High level communication standard. ISO1 = ISO 15118, DIN = DIN 70121
     */
    evCommunicationStandard?: 'DIN' | 'ISO1' | 'ISODIS';
    /**
     * How many seconds until battery capacity reaches xx% of the total (xx usually 80), or undefined if not known
     */
    timeToBulk?: number;
    /**
     * How many seconds until battery is full, or undefined if not known
     */
    timeToFull?: number;
    /**
     * EV identification
     */
    evIdentify?: {
        /**
         * Library version
         */
        identifierVersion?: string;
        possibleEvs?: EvIdentifyEntry[];
        /**
         * This is the EV
         */
        exactMatch?: {
            id?: string;
            displayName?: string;
            make?: string;
            model?: string;
            protocol?: string;
            batteryCapacity?: {
                gross?: number;
                net?: number;
            };
            probability?: number;
            voltage?: any;
            powerWMax?: number;
            targetVoltage?: any;
            targetCurrent?: any;
            currentMax?: number;
            evRunId?: {
                possibilities?: string[];
                patterns?: string[];
            };
            evMacAddressPrefix?: {
                possibilities?: string[];
            };
            evMaximumVoltage?: any;
            evMaximumCurrent?: any;
            evMaximumPower?: any;
            timeToFull?: {
                min?: number;
                max?: number;
            };
            timeToBulk?: any;
        };
        /**
         * Input based on which this identification was performed
         */
        chargingLog: {
            powerW: number;
            evMacAddress: string;
            SoC: number;
            targetVoltage: number;
            current: number;
            targetCurrent: number;
            evMaximumVoltage: number;
            evMaximumCurrent: number;
            voltage: number;
            timeToFull: number;
            evRunId: string;
            evMaximumPower: number;
            timeToBulk: number;
        };
        displayName?: string;
        macInfo?: MacVendorEntry;
    };
    triggerReason?: string;
    /**
     * the raw combined session object
     */
    rawSession?: {
        [k: string]: any;
    };
    /**
     * True if cable has been connected to EV at any point of this session.
     * May be inaccurate for CHAdeMO.
     */
    cableConnected?: boolean;
    /**
     * Method of authorization
     */
    authorizationMethod?: 'EVMAC' | 'IMPLICIT' | 'REMOTE_AUTH' | 'RFID_TOKEN';
    /**
     * True if user had authenticated at any point (be it in free mode, or auth accepted for rfid/remote)
     */
    authenticated?: boolean;
    /**
     * e.g. NegotiationError - InsulationTest:FAILED_VOLTAGE_RAISE
     */
    endReasonDescription?: string;
    /**
     * firmware version this tx was performed with
     */
    firmwareVersion?: string;
    /**
     * Error code reported by EV
     */
    evErrorCode?: string;
    /**
     * Type of the EMV (Europay, MasterCard, Visa) Device. Only set if there is EMV present
     */
    emvDeviceType?: 'PAYTER';
    /**
     * ISO-4217 code of the currency used
     */
    paymentCurrency?: string;
    /**
     * Session max cost in cents / reserved amount of cents.
     * Cent = one-hundredth of the used main currency,
     * example 1 cent = 1 penny for £ and 1 cent = 1 cent for €
     */
    maxCostCents?: number;
    /**
     * Can't be greater than maxCostCents
     */
    totalCostCents?: number;
    /**
     * UUID of the payment
     */
    paymentReference?: number;
    /**
     * Unique ID for the EMV device, payment session start time in seconds
     */
    paymentSessionId?: number;
    waitingForPaymentReservation?: boolean;
    /**
     * State of the payment
     * * `COMMITTED`- The payment was committed
     * * `CANCELLED`- The payment was cancelled
     * * `VOIDED`- The payment was voided
     */
    paymentState?: 'CANCELLED' | 'COMMITTED' | 'VOIDED';
}

export interface ChargingForecast {
    /**
     * Timestamp when specific SoC (key) is reached
     */
    chargeTimes: {
        '80': string;
        '85': string;
        '90': string;
        '95': string;
        '100': string;
    };
    histogram: {
        /**
         * Time in milliseconds from charging startTimeStamp
         */
        x: number;
        /**
         * SoC prediction
         */
        y: number;
    }[];
}

export interface EvIdentifyEntry {
    id?: string;
    displayName?: string;
    make?: string;
    model?: string;
    protocol?: string;
    batteryCapacity?: {
        gross?: number;
        net?: number;
    };
    probability?: number;
    voltage?: any;
    powerWMax?: number;
    targetVoltage?: any;
    targetCurrent?: any;
    currentMax?: number;
    evRunId?: {
        possibilities?: string[];
        patterns?: string[];
    };
    evMacAddressPrefix?: {
        possibilities?: string[];
    };
    evMaximumVoltage?: any;
    evMaximumCurrent?: any;
    evMaximumPower?: any;
    timeToFull?: {
        min?: number;
        max?: number;
    };
    timeToBulk?: any;
}

export interface EvChargingLog {
    powerW: number;
    evMacAddress: string;
    SoC: number;
    targetVoltage: number;
    current: number;
    targetCurrent: number;
    evMaximumVoltage: number;
    evMaximumCurrent: number;
    voltage: number;
    timeToFull: number;
    evRunId: string;
    evMaximumPower: number;
    timeToBulk: number;
}

export interface MacVendorEntry {
    macPrefix: string;
    macVendor: string;
    knownEvs: string[];
}

export interface MaintenanceApiService {
    axios: AxiosInstance;
    baseURL: string;
    jwtToken: string;
}

export interface ChangeAvailabilityRequest {
    type: 'Inoperative' | 'Operative';
}

export interface ChangeAvailabilityResponse {
    responseFromStation: 'Accepted' | 'Rejected' | 'Scheduled';
}

export interface ClearChargingProfilesRequest {
    chargingProfileCriteria?: ClearChargingProfileType;
    chargingProfileId?: number;
}

export interface ClearChargingProfilesResponse {
    response: ClearChargingProfileStatusEnumType;
    statusInfo?: StatusInfoType;
}

export interface DeletePowerLimitResponse {
    response: ClearChargingProfileStatusEnumType;
    statusInfo?: StatusInfoType;
}

export interface GetChargingProfileQueryParameters {
    chargingProfilePurpose?: ChargingProfilePurposeEnumType;
    /**
     * Charging_ Profile. Stack_ Level. Counter
     * urn:x-oca:ocpp:uid:1:569230
     * Value determining level in hierarchy stack of profiles. Higher values have precedence over lower values. Lowest level is 0.
     */
    stackLevel?: number;
    /**
     * List of all the chargingProfileIds requested. Any ChargingProfile that matches one of these profiles will be reported. If omitted, the Charging Station SHALL not filter on chargingProfileId. This field SHALL NOT contain more ids than set in &lt;&lt;configkey-charging-profile-entries,ChargingProfileEntries.maxLimit&gt;&gt;
     */
    chargingProfileId?: number[];
    /**
     * For which charging limit sources, charging profiles SHALL be reported. If omitted, the Charging Station SHALL not filter on chargingLimitSource.
     */
    chargingLimitSource?: ('CSO' | 'EMS' | 'Other' | 'SO')[];
}

export interface GetChargingProfileResponse {
    status: GetChargingProfileStatusEnumType;
    profiles: {
        evseId: number;
        chargingLimitSource: ChargingLimitSourceEnumType;
        profiles: ChargingProfileType[];
    }[];
}

export interface GetChargingProfileScheduleQueryParameters {
    duration?: number;
}

export interface GetChargingProfileScheduleResponse {
    status: GenericStatusEnumType;
    schedule: CompositeScheduleType;
}

export interface GetFileResponse {
    contents: string;
}

export interface GetScreenshotsResponse {
    captures: ScreenshotCapture[];
}

export interface PingStationResponse {
    success: true;
    latency?: number;
}

export interface PutChargingProfileRequest {
    chargingProfile: ChargingProfileType;
}

export interface PutChargingProfileResponse {
    response: ChargingProfileStatusEnumType;
    statusInfo?: StatusInfoType_1;
}

export interface PutFileRequest {
    contents: string;
}

export interface PutFileResponse {
    accepted: true;
}

export interface PowerLimitationDcKw {
    /**
     * Maximum DC charging power
     */
    dcMaxPowerKw: number;
}

export interface PowerLimitationAcKw {
    /**
     * Maximum AC power draw
     */
    acMaxPowerKw: number;
}

export interface PowerLimitationAcA {
    /**
     * Maximum AC current draw
     */
    acMaxPowerA: number;
}

export type PowerLimitation =
    | {
          /**
           * Maximum DC charging power
           */
          dcMaxPowerKw: number;
      }
    | {
          /**
           * Maximum AC power draw
           */
          acMaxPowerKw: number;
      }
    | {
          /**
           * Maximum AC current draw
           */
          acMaxPowerA: number;
      };

export interface PutPowerLimitRequest {
    limit:
        | {
              /**
               * Maximum DC charging power
               */
              dcMaxPowerKw: number;
          }
        | {
              /**
               * Maximum AC power draw
               */
              acMaxPowerKw: number;
          }
        | {
              /**
               * Maximum AC current draw
               */
              acMaxPowerA: number;
          };
}

export interface PutPowerLimitResponse {
    response: ChargingProfileStatusEnumType;
    statusInfo?: StatusInfoType;
}

export interface RequestStationAuthTokenResponse {
    token: string;
    tokenPayload: DeviceAuthContext;
}

export interface DeviceAuthContext {
    /**
     * Device serial number
     */
    sub: string;
    /**
     * Issuer
     */
    iss: string;
    /**
     * Token ID
     */
    jti: string;
    /**
     * Issued at, unix timestamp
     */
    iat: number;
    /**
     * Expiry, unix timestamp
     */
    exp?: number;
}

export interface RestartStationRequest {
    /**
     * How to perform the restart?
     *
     *    * Immediate: Immediate reset of the Charging Station
     *    * OnIdle: Delay reset until no more transactions are active
     */
    type: 'Immediate' | 'OnIdle';
}

export interface RestartStationResponse {
    /**
     * * Accepted: Command will be executed.
     * * Rejected: Command will not be executed.
     * * Scheduled: Reset command is scheduled, Charging Station is busy with a process that cannot be interrupted at the moment. Reset will be executed when process is finished.
     */
    responseFromStation: 'Accepted' | 'Rejected' | 'Scheduled';
}

export interface ScreenshotResponse {
    url: string;
}

export interface ScreenshotQueryParameters {
    satelliteId?: number;
    id?: string;
}

export interface TransactionStartRequest {
    chargingProfile?: ChargingProfileType;
    idToken?: string;
}

export interface TransactionStartResponse {
    transactionStartResponse: RequestStartTransactionResponse;
}

export interface TransactionStopResponse {
    transactionStopResponse: RequestStopTransactionResponse;
}

export interface UnlockRequest {}

export interface UnlockResponse {
    responseFromStation: 'UnlockFailed' | 'Unlocked';
}

/**
 * Charging_ Profile
 * urn:x-oca:ocpp:uid:2:233255
 * A ChargingProfile consists of a ChargingSchedule, describing the amount of power or current that can be delivered per time interval.
 */
export interface ClearChargingProfileType {
    customData?: CustomDataType;
    /**
     * Identified_ Object. MRID. Numeric_ Identifier
     * urn:x-enexis:ecdm:uid:1:569198
     * Specifies the id of the EVSE for which to clear charging profiles. An evseId of zero (0) specifies the charging profile for the overall Charging Station. Absence of this parameter means the clearing applies to all charging profiles that match the other criteria in the request.
     */
    evseId?: number;
    chargingProfilePurpose?: ChargingProfilePurposeEnumType;
    /**
     * Charging_ Profile. Stack_ Level. Counter
     * urn:x-oca:ocpp:uid:1:569230
     * Specifies the stackLevel for which charging profiles will be cleared, if they meet the other criteria in the request.
     */
    stackLevel?: number;
}

/**
 * This class does not get 'AdditionalProperties = false' in the schema generation, so it can be extended with arbitrary JSON properties to allow adding custom data.
 */
export interface CustomDataType {
    vendorId: string;
    [k: string]: any;
}

/**
 * Charging_ Profile. Charging_ Profile_ Purpose. Charging_ Profile_ Purpose_ Code
 * urn:x-oca:ocpp:uid:1:569231
 * Specifies to purpose of the charging profiles that will be cleared, if they meet the other criteria in the request.
 */
export type ChargingProfilePurposeEnumType =
    | 'ChargingStationExternalConstraints'
    | 'ChargingStationMaxProfile'
    | 'TxDefaultProfile'
    | 'TxProfile';

/**
 * Indicates if the Charging Station was able to execute the request.
 */
export type ClearChargingProfileStatusEnumType = 'Accepted' | 'Unknown';

/**
 * Element providing more information about the status.
 */
export interface StatusInfoType {
    customData?: CustomDataType_1;
    /**
     * A predefined code for the reason why the status is returned in this response. The string is case-insensitive.
     */
    reasonCode: string;
    /**
     * Additional text to provide detailed information.
     */
    additionalInfo?: string;
}

/**
 * This class does not get 'AdditionalProperties = false' in the schema generation, so it can be extended with arbitrary JSON properties to allow adding custom data.
 */
export interface CustomDataType_1 {
    vendorId: string;
    [k: string]: any;
}

/**
 * This indicates whether the Charging Station is able to process this request and will send &lt;&lt;reportchargingprofilesrequest, ReportChargingProfilesRequest&gt;&gt; messages.
 */
export type GetChargingProfileStatusEnumType = 'Accepted' | 'NoProfiles';

/**
 * Source that has installed this charging profile.
 */
export type ChargingLimitSourceEnumType = 'CSO' | 'EMS' | 'Other' | 'SO';

/**
 * Charging_ Profile
 * urn:x-oca:ocpp:uid:2:233255
 * A ChargingProfile consists of ChargingSchedule, describing the amount of power or current that can be delivered per time interval.
 */
export interface ChargingProfileType {
    customData?: CustomDataType_2;
    /**
     * Identified_ Object. MRID. Numeric_ Identifier
     * urn:x-enexis:ecdm:uid:1:569198
     * Id of ChargingProfile.
     */
    id: number;
    /**
     * Charging_ Profile. Stack_ Level. Counter
     * urn:x-oca:ocpp:uid:1:569230
     * Value determining level in hierarchy stack of profiles. Higher values have precedence over lower values. Lowest level is 0.
     */
    stackLevel: number;
    chargingProfilePurpose: ChargingProfilePurposeEnumType;
    chargingProfileKind: ChargingProfileKindEnumType;
    recurrencyKind?: RecurrencyKindEnumType;
    /**
     * Charging_ Profile. Valid_ From. Date_ Time
     * urn:x-oca:ocpp:uid:1:569234
     * Point in time at which the profile starts to be valid. If absent, the profile is valid as soon as it is received by the Charging Station.
     */
    validFrom?: string;
    /**
     * Charging_ Profile. Valid_ To. Date_ Time
     * urn:x-oca:ocpp:uid:1:569235
     * Point in time at which the profile stops to be valid. If absent, the profile is valid until it is replaced by another profile.
     */
    validTo?: string;
    chargingSchedule: ChargingScheduleType[];
    /**
     * SHALL only be included if ChargingProfilePurpose is set to TxProfile. The transactionId is used to match the profile to a specific transaction.
     */
    transactionId?: string;
}

/**
 * This class does not get 'AdditionalProperties = false' in the schema generation, so it can be extended with arbitrary JSON properties to allow adding custom data.
 */
export interface CustomDataType_2 {
    vendorId: string;
    [k: string]: any;
}

/**
 * Charging_ Profile. Charging_ Profile_ Kind. Charging_ Profile_ Kind_ Code
 * urn:x-oca:ocpp:uid:1:569232
 * Indicates the kind of schedule.
 */
export type ChargingProfileKindEnumType = 'Absolute' | 'Recurring' | 'Relative';

/**
 * Charging_ Profile. Recurrency_ Kind. Recurrency_ Kind_ Code
 * urn:x-oca:ocpp:uid:1:569233
 * Indicates the start point of a recurrence.
 */
export type RecurrencyKindEnumType = 'Daily' | 'Weekly';

/**
 * Charging_ Schedule
 * urn:x-oca:ocpp:uid:2:233256
 * Charging schedule structure defines a list of charging periods, as used in: GetCompositeSchedule.conf and ChargingProfile.
 */
export interface ChargingScheduleType {
    customData?: CustomDataType_2;
    /**
     * Identifies the ChargingSchedule.
     */
    id: number;
    /**
     * Charging_ Schedule. Start_ Schedule. Date_ Time
     * urn:x-oca:ocpp:uid:1:569237
     * Starting point of an absolute schedule. If absent the schedule will be relative to start of charging.
     */
    startSchedule?: string;
    /**
     * Charging_ Schedule. Duration. Elapsed_ Time
     * urn:x-oca:ocpp:uid:1:569236
     * Duration of the charging schedule in seconds. If the duration is left empty, the last period will continue indefinitely or until end of the transaction if chargingProfilePurpose = TxProfile.
     */
    duration?: number;
    chargingRateUnit: ChargingRateUnitEnumType;
    chargingSchedulePeriod: ChargingSchedulePeriodType[];
    /**
     * Charging_ Schedule. Min_ Charging_ Rate. Numeric
     * urn:x-oca:ocpp:uid:1:569239
     * Minimum charging rate supported by the EV. The unit of measure is defined by the chargingRateUnit. This parameter is intended to be used by a local smart charging algorithm to optimize the power allocation for in the case a charging process is inefficient at lower charging rates. Accepts at most one digit fraction (e.g. 8.1)
     */
    minChargingRate?: number;
    salesTariff?: SalesTariffType;
}

/**
 * Charging_ Schedule. Charging_ Rate_ Unit. Charging_ Rate_ Unit_ Code
 * urn:x-oca:ocpp:uid:1:569238
 * The unit of measure Limit is expressed in.
 */
export type ChargingRateUnitEnumType = 'A' | 'W';

/**
 * Charging_ Schedule_ Period
 * urn:x-oca:ocpp:uid:2:233257
 * Charging schedule period structure defines a time period in a charging schedule.
 */
export interface ChargingSchedulePeriodType {
    customData?: CustomDataType_2;
    /**
     * Charging_ Schedule_ Period. Start_ Period. Elapsed_ Time
     * urn:x-oca:ocpp:uid:1:569240
     * Start of the period, in seconds from the start of schedule. The value of StartPeriod also defines the stop time of the previous period.
     */
    startPeriod: number;
    /**
     * Charging_ Schedule_ Period. Limit. Measure
     * urn:x-oca:ocpp:uid:1:569241
     * Charging rate limit during the schedule period, in the applicable chargingRateUnit, for example in Amperes (A) or Watts (W). Accepts at most one digit fraction (e.g. 8.1).
     */
    limit: number;
    /**
     * Charging_ Schedule_ Period. Number_ Phases. Counter
     * urn:x-oca:ocpp:uid:1:569242
     * The number of phases that can be used for charging. If a number of phases is needed, numberPhases=3 will be assumed unless another number is given.
     */
    numberPhases?: number;
    /**
     * Values: 1..3, Used if numberPhases=1 and if the EVSE is capable of switching the phase connected to the EV, i.e. ACPhaseSwitchingSupported is defined and true. It’s not allowed unless both conditions above are true. If both conditions are true, and phaseToUse is omitted, the Charging Station / EVSE will make the selection on its own.
     */
    phaseToUse?: number;
}

/**
 * Sales_ Tariff
 * urn:x-oca:ocpp:uid:2:233272
 * NOTE: This dataType is based on dataTypes from &lt;&lt;ref-ISOIEC15118-2,ISO 15118-2&gt;&gt;.
 */
export interface SalesTariffType {
    customData?: CustomDataType_2;
    /**
     * Identified_ Object. MRID. Numeric_ Identifier
     * urn:x-enexis:ecdm:uid:1:569198
     * SalesTariff identifier used to identify one sales tariff. An SAID remains a unique identifier for one schedule throughout a charging session.
     */
    id: number;
    /**
     * Sales_ Tariff. Sales. Tariff_ Description
     * urn:x-oca:ocpp:uid:1:569283
     * A human readable title/short description of the sales tariff e.g. for HMI display purposes.
     */
    salesTariffDescription?: string;
    /**
     * Sales_ Tariff. Num_ E_ Price_ Levels. Counter
     * urn:x-oca:ocpp:uid:1:569284
     * Defines the overall number of distinct price levels used across all provided SalesTariff elements.
     */
    numEPriceLevels?: number;
    salesTariffEntry: SalesTariffEntryType[];
}

/**
 * Sales_ Tariff_ Entry
 * urn:x-oca:ocpp:uid:2:233271
 */
export interface SalesTariffEntryType {
    customData?: CustomDataType_2;
    relativeTimeInterval: RelativeTimeIntervalType;
    /**
     * Sales_ Tariff_ Entry. E_ Price_ Level. Unsigned_ Integer
     * urn:x-oca:ocpp:uid:1:569281
     * Defines the price level of this SalesTariffEntry (referring to NumEPriceLevels). Small values for the EPriceLevel represent a cheaper TariffEntry. Large values for the EPriceLevel represent a more expensive TariffEntry.
     */
    ePriceLevel?: number;
    consumptionCost?: ConsumptionCostType[];
}

/**
 * Relative_ Timer_ Interval
 * urn:x-oca:ocpp:uid:2:233270
 */
export interface RelativeTimeIntervalType {
    customData?: CustomDataType_2;
    /**
     * Relative_ Timer_ Interval. Start. Elapsed_ Time
     * urn:x-oca:ocpp:uid:1:569279
     * Start of the interval, in seconds from NOW.
     */
    start: number;
    /**
     * Relative_ Timer_ Interval. Duration. Elapsed_ Time
     * urn:x-oca:ocpp:uid:1:569280
     * Duration of the interval, in seconds.
     */
    duration?: number;
}

/**
 * Consumption_ Cost
 * urn:x-oca:ocpp:uid:2:233259
 */
export interface ConsumptionCostType {
    customData?: CustomDataType_2;
    /**
     * Consumption_ Cost. Start_ Value. Numeric
     * urn:x-oca:ocpp:uid:1:569246
     * The lowest level of consumption that defines the starting point of this consumption block. The block interval extends to the start of the next interval.
     */
    startValue: number;
    cost: CostType[];
}

/**
 * Cost
 * urn:x-oca:ocpp:uid:2:233258
 */
export interface CostType {
    customData?: CustomDataType_2;
    costKind: CostKindEnumType;
    /**
     * Cost. Amount. Amount
     * urn:x-oca:ocpp:uid:1:569244
     * The estimated or actual cost per kWh
     */
    amount: number;
    /**
     * Cost. Amount_ Multiplier. Integer
     * urn:x-oca:ocpp:uid:1:569245
     * Values: -3..3, The amountMultiplier defines the exponent to base 10 (dec). The final value is determined by: amount * 10 ^ amountMultiplier
     */
    amountMultiplier?: number;
}

/**
 * Cost. Cost_ Kind. Cost_ Kind_ Code
 * urn:x-oca:ocpp:uid:1:569243
 * The kind of cost referred to in the message element amount
 */
export type CostKindEnumType = 'CarbonDioxideEmission' | 'RelativePricePercentage' | 'RenewableGenerationPercentage';

/**
 * The Charging Station will indicate if it was
 * able to process the request
 */
export type GenericStatusEnumType = 'Accepted' | 'Rejected';

/**
 * Composite_ Schedule
 * urn:x-oca:ocpp:uid:2:233362
 */
export interface CompositeScheduleType {
    customData?: CustomDataType_3;
    chargingSchedulePeriod: ChargingSchedulePeriodType_1[];
    /**
     * The ID of the EVSE for which the
     * schedule is requested. When evseid=0, the
     * Charging Station calculated the expected
     * consumption for the grid connection.
     */
    evseId: number;
    /**
     * Duration of the schedule in seconds.
     */
    duration: number;
    /**
     * Composite_ Schedule. Start. Date_ Time
     * urn:x-oca:ocpp:uid:1:569456
     * Date and time at which the schedule becomes active. All time measurements within the schedule are relative to this timestamp.
     */
    scheduleStart: string;
    chargingRateUnit: ChargingRateUnitEnumType;
}

/**
 * This class does not get 'AdditionalProperties = false' in the schema generation, so it can be extended with arbitrary JSON properties to allow adding custom data.
 */
export interface CustomDataType_3 {
    vendorId: string;
    [k: string]: any;
}

/**
 * Charging_ Schedule_ Period
 * urn:x-oca:ocpp:uid:2:233257
 * Charging schedule period structure defines a time period in a charging schedule.
 */
export interface ChargingSchedulePeriodType_1 {
    customData?: CustomDataType_3;
    /**
     * Charging_ Schedule_ Period. Start_ Period. Elapsed_ Time
     * urn:x-oca:ocpp:uid:1:569240
     * Start of the period, in seconds from the start of schedule. The value of StartPeriod also defines the stop time of the previous period.
     */
    startPeriod: number;
    /**
     * Charging_ Schedule_ Period. Limit. Measure
     * urn:x-oca:ocpp:uid:1:569241
     * Charging rate limit during the schedule period, in the applicable chargingRateUnit, for example in Amperes (A) or Watts (W). Accepts at most one digit fraction (e.g. 8.1).
     */
    limit: number;
    /**
     * Charging_ Schedule_ Period. Number_ Phases. Counter
     * urn:x-oca:ocpp:uid:1:569242
     * The number of phases that can be used for charging. If a number of phases is needed, numberPhases=3 will be assumed unless another number is given.
     */
    numberPhases?: number;
    /**
     * Values: 1..3, Used if numberPhases=1 and if the EVSE is capable of switching the phase connected to the EV, i.e. ACPhaseSwitchingSupported is defined and true. It’s not allowed unless both conditions above are true. If both conditions are true, and phaseToUse is omitted, the Charging Station / EVSE will make the selection on its own.
     */
    phaseToUse?: number;
}

export interface ScreenshotCapture {
    id: string;
    satellites: {
        [k: string]: string;
    };
}

/**
 * Returns whether the Charging Station has been able to process the message successfully. This does not guarantee the schedule will be followed to the letter. There might be other constraints the Charging Station may need to take into account.
 */
export type ChargingProfileStatusEnumType = 'Accepted' | 'Rejected';

/**
 * Element providing more information about the status.
 */
export interface StatusInfoType_1 {
    customData?: CustomDataType_4;
    /**
     * A predefined code for the reason why the status is returned in this response. The string is case-insensitive.
     */
    reasonCode: string;
    /**
     * Additional text to provide detailed information.
     */
    additionalInfo?: string;
}

/**
 * This class does not get 'AdditionalProperties = false' in the schema generation, so it can be extended with arbitrary JSON properties to allow adding custom data.
 */
export interface CustomDataType_4 {
    vendorId: string;
    [k: string]: any;
}

export interface RequestStartTransactionResponse {
    status: 'Accepted' | 'Rejected';
    transactionId?: string;
    [k: string]: any;
}

export interface RequestStopTransactionResponse {
    status: 'Accepted' | 'Rejected';
    [k: string]: any;
}

export type CommandsRequest = CancelReservation | ReserveNow | StartSession | StopSession | UnlockConnector;

export interface CommandsResponse {
    status_code:
        | 1000
        | 1900
        | 2000
        | 2001
        | 2002
        | 2003
        | 2004
        | 2900
        | 3000
        | 3001
        | 3002
        | 3003
        | 3900
        | 4001
        | 4002
        | 4003
        | 4900;
    data?: CommandResponse | null;
    status_message?: string | null;
    timestamp: string;
}

export interface StartSessionRequest {
    response_url: string;
    token: Token;
    location_id: string;
    evse_uid?: string | null;
    authorization_reference?: string | null;
}

export interface StartSessionResponse {
    result: CommandResponseType;
    message?: DisplayText[] | null;
}

export interface StopSessionRequest {
    response_url: string;
    session_id: string;
}

export interface StopSessionResponse {
    result: CommandResponseType;
    message?: DisplayText[] | null;
}

export interface GetLocationResponse {
    status_code:
        | 1000
        | 1900
        | 2000
        | 2001
        | 2002
        | 2003
        | 2004
        | 2900
        | 3000
        | 3001
        | 3002
        | 3003
        | 3900
        | 4001
        | 4002
        | 4003
        | 4900;
    data?: Location | null;
    status_message?: string | null;
    timestamp: string;
}
/** **/

type JobStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'PARTIALLY_FAILED' | 'STOPPED' | 'STOPPING';

export interface PullTokensStatusBase {
    /**
     * Keep track of the last token pull as per the OCPI GET Tokens Pull Model
     */
    jobId: string; // Last ran/pending jobId
    status: JobStatus;
    initiateTime: string; // ISO 8601 timestamp when the job was initiated
    startTime?: string; // ISO 8601 timestamp when the job started
    endTime?: string; // ISO 8601 timestamp when the job ended/completed
    lastUpdatedTime: string; // ISO 8601 timestamp when the job record was last updated
    totalCount?: number; // Total number of tokens to be pulled/pulled for the current job/last job
    pages?: number; // Total number of pages to be pulled/pulled for the current job/last job
    dateFrom?: string; // Filter date_from used for GET Tokens request
    dateTo?: string; // Filter date_to used for GET Tokens request
    failureReason?: string; // Reason for failure for the whole job/all pages
    failedPages?: {
        // Details of failed pages
        offset: number; // The starting offset of the failed page
        limit: number; // The limit of the failed page (usually X-Limit)
        failureReason?: string; // Failure reason for the page
    }[]; // Pages that failed while running a pullAndPushTokens job
    sqsEvents?: number; // Total number of sqsEvents for the job
    sqsEventsProcessed?: number; // Number of batches processed so far
    tokensProcessed?: number; // Number of tokens processed/upserted
}

export type PullTokensOfProfileResponse = {
    status_code: number;
    status_message: string;
    totalCount?: number;
    pages?: number /* number of GET token calls */;
    sqsEvents?: number /* number of SQS events */;
    jobId: string;
};

export interface PullTokensOfProfileQueryParameters {
    jobId?: string;
    date_from?: string;
    date_to?: string;
}

type PULL_ACTION =
    | 'COMPLETE_REFRESH'
    | 'RERUN_FAILED'
    | 'PARTIAL_REFRESH'
    | 'STOP_RUN'
    | 'STOP_JOB'
    | 'GET_STATUS'
    | 'GET_JOBS'
    | 'DELETE_JOB'
    | 'UPDATE_JOB'
    | 'GET_JOB';

export interface ManageTokenPullQueryParameters extends PullTokensOfProfileQueryParameters {
    numJobs?: number;
    status?: PullTokensStatusBase['status'];
}

export type ManageTokenPullResponse = {
    message: string;
    jobs?: PullTokensStatusBase[];
};

/** **/

export interface Location {
    country_code: string;
    party_id: string;
    id: string;
    publish: boolean;
    publish_allowed_to?: any[] | null;
    name?: string | null;
    address: string;
    city: string;
    postal_code?: string | null;
    state?: string | null;
    country: string;
    coordinates: GeoLocation;
    related_locations?: any[] | null;
    parking_type?: null;
    evses: Evse[];
    directions?: DisplayText[] | null;
    operator?: null;
    suboperator?: null;
    owner?: null;
    facilities?: any[] | null;
    time_zone: string;
    opening_times?: null;
    charging_when_closed?: boolean | null;
    images?: any[] | null;
    energy_mix?: null;
    last_updated: string;
}

export interface GeoLocation {
    latitude: string;
    longitude: string;
}

export interface Evse {
    uid: string;
    evse_id?: string | null;
    status: Status;
    status_schedule?: any[] | null;
    capabilities?: any[] | null;
    connectors: Connector[];
    floor_level?: string | null;
    coordinates?: GeoLocation | null;
    physical_reference?: string | null;
    directions?: any[] | null;
    parking_restrictions?: null;
    images?: any[] | null;
    last_updated: string;
}

export interface Connector {
    id: string;
    standard: ConnectorType;
    format: ConnectorFormat;
    power_type: PowerType;
    max_voltage: number;
    max_amperage: number;
    max_electric_power?: number | null;
    tariff_ids?: string[] | null;
    terms_and_conditions?: string | null;
    last_updated: string;
    /**
     * OCPI EXTENSIONS
     */
    custom: {
        physical_reference?: string | null;
        vehicle?: {
            model?: string | null;
        } | null;
    };
}

export type ConnectorType =
    | 'CHADEMO'
    | 'DOMESTIC_A'
    | 'DOMESTIC_B'
    | 'DOMESTIC_C'
    | 'DOMESTIC_D'
    | 'DOMESTIC_E'
    | 'DOMESTIC_F'
    | 'DOMESTIC_G'
    | 'DOMESTIC_H'
    | 'DOMESTIC_I'
    | 'DOMESTIC_J'
    | 'DOMESTIC_K'
    | 'DOMESTIC_L'
    | 'IEC_60309_2_single_16'
    | 'IEC_60309_2_three_16'
    | 'IEC_60309_2_three_32'
    | 'IEC_60309_2_three_64'
    | 'IEC_62196_T1'
    | 'IEC_62196_T1_COMBO'
    | 'IEC_62196_T2'
    | 'IEC_62196_T2_COMBO'
    | 'IEC_62196_T3A'
    | 'IEC_62196_T3C'
    | 'PANTOGRAPH_BOTTOM_'
    | 'PANTOGRAPH_TOP_DOWN'
    | 'TESLA_R'
    | 'TESLA_S'
    | 'UP';

export type ConnectorFormat = 'CABLE' | 'SOCKET';

export type PowerType = 'AC_1_PHASE' | 'AC_3_PHASE' | 'DC';

export interface DisplayText {
    language: string;
    text: string;
}

export interface AxiosInstance {
    defaults: AxiosRequestConfig;
    interceptors: {
        request: AxiosInterceptorManagerAxiosRequestConfig;
        response: AxiosInterceptorManagerAxiosResponseAny;
    };
}

export interface AxiosRequestConfig {
    url?: string;
    method?: Method;
    baseURL?: string;
    transformRequest?: AxiosTransformer | AxiosTransformer[];
    transformResponse?: AxiosTransformer | AxiosTransformer[];
    headers?: any;
    params?: any;
    paramsSerializer?: {};
    data?: any;
    timeout?: number;
    timeoutErrorMessage?: string;
    withCredentials?: boolean;
    adapter?: AxiosAdapter;
    auth?: AxiosBasicCredentials;
    responseType?: ResponseType;
    xsrfCookieName?: string;
    xsrfHeaderName?: string;
    onUploadProgress?: {};
    onDownloadProgress?: {};
    maxContentLength?: number;
    validateStatus?: {};
    maxBodyLength?: number;
    maxRedirects?: number;
    socketPath?: string;
    httpAgent?: any;
    httpsAgent?: any;
    proxy?: AxiosProxyConfig | false;
    cancelToken?: CancelToken;
    decompress?: boolean;
}

export type Method =
    | 'DELETE'
    | 'GET'
    | 'HEAD'
    | 'LINK'
    | 'OPTIONS'
    | 'PATCH'
    | 'POST'
    | 'PURGE'
    | 'PUT'
    | 'UNLINK'
    | 'delete'
    | 'get'
    | 'head'
    | 'link'
    | 'options'
    | 'patch'
    | 'post'
    | 'purge'
    | 'put'
    | 'unlink';

export interface AxiosTransformer {}

export interface AxiosAdapter {}

export interface AxiosBasicCredentials {
    username: string;
    password: string;
}

export type ResponseType = 'arraybuffer' | 'blob' | 'document' | 'json' | 'stream' | 'text';

export interface AxiosProxyConfig {
    host: string;
    port: number;
    auth?: {
        username: string;
        password: string;
    };
    protocol?: string;
}

export interface CancelToken {
    promise: PromiseCancel;
    reason?: Cancel;
}

/**
 * Represents the completion of an asynchronous operation
 * Represents the completion of an asynchronous operation
 */
export interface PromiseCancel {
    '__@toStringTag': string;
}

export interface Cancel {
    message: string;
}

export interface AxiosInterceptorManagerAxiosRequestConfig {}

export interface AxiosInterceptorManagerAxiosResponseAny {}

export interface StatusRow_1 {
    schema: 'status';
    /**
     * When status became active.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    statusDate: string;
    /**
     * Status of the EVSE or Charging station.
     */
    status: 'Available' | 'Charging' | 'Faulted' | 'Occupied' | 'Offline' | 'Operative' | 'Reserved' | 'Unavailable';
    /**
     * Reason for why the status is unavailable
     */
    reason?: string;
    /**
     * More detailed reason why status is unavailable
     */
    detail?: string;
    /**
     * EVSE unique identifier.
     */
    evseId: string;
    /**
     * Name of the evse/connector
     */
    evseName?: string;
    /**
     * Type of the evse/connector
     */
    evseType?: 'AC' | 'CCS' | 'CHAdeMO' | 'Type2' | 'oppcharge';
    /**
     * Max connector cable current, e.g. 100 or 125 A
     */
    maxCableCurrent?: number;
    /**
     * Maximum power in kilowatts that this connector could output
     */
    maxPowerKw?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics)
     */
    maxPowerkWPresent?: number;
    /**
     * Maximum power in kilowatts that this connector could output (unrounded figure)
     */
    maxPowerkWActual?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics) (unrounded figure)
     */
    maxPowerkWPresentActual?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed
     */
    maxPowerKwWithoutDynamicLimit?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed (unrounded figure)
     */
    maxPowerKwWithoutDynamicLimitActual?: number;
    /**
     * Actual EVSE identifier - that is, EVSE as in can output to one EV at a time.
     * If multiple "chargeye evses" share this same evseNumber then they belong to the same EVSE.
     * This can be the case for e.g. X-satellites or some Trolleys configured as Sequential.
     */
    actualEVSEId?: number;
    /**
     * Maximum voltage this EVSE can output (500 or 800V)
     */
    maxVoltage?: number;
    /**
     * Used to deduce whether or not the evse is currently in active configuration
     */
    configurationId?: number;
    /**
     * Config - Static text to display instead of the Maximum power in kW (e.g. 40 kW)
     */
    MaxPowerDisplay?: string;
    /**
     * Config -Short additional text to display on the charger
     */
    SupplementaryNote?: string;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    _pk: string;
    _sk: string;
    _gsiTenantEMSPPk: string;
    _gsiTenantEMSPSk: string;
    _gsiTenantCPOPk: string;
    _gsiTenantCPOSk: string;
    _gsiTenantMaintenancePk: string;
    _gsiTenantMaintenanceSk: string;
    _gsi1pk: string;
    _gsi1sk: string;
    tenantIdEMSP: string;
    tenantIdCPO: string;
    tenantIdMaintenance: string;
    schemaVersion?: number;
    versionCreatedTimestamp: string;
    versionCreatedUserId: string;
    versionModifiedTimestamp: string;
    versionModifiedUserId: string;
    versionDeletedTimestamp?: string;
    versionDeletedUserId: string;
    deleted?: boolean;
}

export interface AlertRow_1 {
    schema: 'alert';
    /**
     * When alert became active.
     * e.g. 2019-04-10T06:10:23.234Z
     */
    timestamp: string;
    /**
     * Type of the error (based on OCPP20, if possible)
     */
    type: string;
    /**
     * Error code
     */
    errorCode?: string;
    /**
     * Error message, or other details
     */
    message?: string;
    /**
     * EVSE unique identifier.
     */
    evseId: string;
    /**
     * Name of the evse/connector
     */
    evseName?: string;
    /**
     * Type of the evse/connector
     */
    evseType?: 'AC' | 'CCS' | 'CHAdeMO' | 'Type2' | 'oppcharge';
    /**
     * Max connector cable current, e.g. 100 or 125 A
     */
    maxCableCurrent?: number;
    /**
     * Maximum power in kilowatts that this connector could output
     */
    maxPowerKw?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics)
     */
    maxPowerkWPresent?: number;
    /**
     * Maximum power in kilowatts that this connector could output (unrounded figure)
     */
    maxPowerkWActual?: number;
    /**
     * Maximum power in kilowatts, effectively, that this connector can output for new sessions (with regards to the current charger usage with dynamics) (unrounded figure)
     */
    maxPowerkWPresentActual?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed
     */
    maxPowerKwWithoutDynamicLimit?: number;
    /**
     * Maximum power in kilowatts that this connector could output if grid limit was not imposed (unrounded figure)
     */
    maxPowerKwWithoutDynamicLimitActual?: number;
    /**
     * Actual EVSE identifier - that is, EVSE as in can output to one EV at a time.
     * If multiple "chargeye evses" share this same evseNumber then they belong to the same EVSE.
     * This can be the case for e.g. X-satellites or some Trolleys configured as Sequential.
     */
    actualEVSEId?: number;
    /**
     * Maximum voltage this EVSE can output (500 or 800V)
     */
    maxVoltage?: number;
    /**
     * Used to deduce whether or not the evse is currently in active configuration
     */
    configurationId?: number;
    /**
     * Config - Static text to display instead of the Maximum power in kW (e.g. 40 kW)
     */
    MaxPowerDisplay?: string;
    /**
     * Config -Short additional text to display on the charger
     */
    SupplementaryNote?: string;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string;
    _pk: string;
    _sk: string;
    _gsiTenantEMSPPk: string;
    _gsiTenantEMSPSk: string;
    _gsiTenantCPOPk: string;
    _gsiTenantCPOSk: string;
    _gsiTenantMaintenancePk: string;
    _gsiTenantMaintenanceSk: string;
    _gsi1pk: string;
    _gsi1sk: string;
    tenantIdEMSP: string;
    tenantIdCPO: string;
    tenantIdMaintenance: string;
    schemaVersion?: number;
    versionCreatedTimestamp: string;
    versionCreatedUserId: string;
    versionModifiedTimestamp: string;
    versionModifiedUserId: string;
    versionDeletedTimestamp?: string;
    versionDeletedUserId: string;
    deleted?: boolean;
}

export interface CancelReservation {
    response_url: string;
    reservation_id: string;
}

export interface ReserveNow {
    reponse_url: string;
    token: Token;
    expiry_date: string;
    reservation_id: string;
    location_id: string;
    evse_uid?: string | null;
    authorization_reference?: string | null;
}

export interface Token {
    country_code: string;
    party_id: string;
    uid: string;
    type: TokenType;
    contract_id: string;
    visual_number?: string | null;
    issuer: string;
    group_id?: string | null;
    valid: boolean;
    whitelist: WhitelistType;
    language?: string | null;
    default_profile_type?: ProfileType | null;
    energy_contract?: EnergyContract | null;
    last_updated: string;
}

export type TokenType = 'AD_HOC_USER' | 'APP_USER' | 'OTHER' | 'RFID';

export type WhitelistType = 'ALLOWED' | 'ALLOWED_OFFLINE' | 'ALWAYS' | 'NEVER';

export type ProfileType = 'CHEAP' | 'FAST' | 'GREEN' | 'REGULAR';

export interface EnergyContract {
    supplier_name: string;
    contract_id: string;
}

export interface StartSession {
    response_url: string;
    token: Token;
    location_id: string;
    evse_uid?: string | null;
    authorization_reference?: string | null;
}

export interface StopSession {
    response_url: string;
    session_id: string;
}

export interface UnlockConnector {
    response_url: string;
    location_id: string;
    evse_uid: string;
    connector_id: string;
}

export interface CommandResponse {
    result: CommandResponseType;
    timeout: number;
    message?: DisplayText[] | null;
}

export type CommandResponseType = 'ACCEPTED' | 'NOT_SUPPORTED' | 'REJECTED' | 'UNKNOWN_SESSION';
